/**
 * Count Session Manager Component
 * 
 * Manages count templates and count sessions for inventory counting.
 * 
 * Recent fix (2024): Removed 'account' field from count_sessions insert
 * as the table uses 'store_id' and 'created_by' for multi-tenancy instead.
 * This fixed the "Error creating count session" issue.
 */

import React, { useState, useEffect, useRef } from 'react';
import supabase from '../config/supabase';
import { useMisuseDetection } from '../utils/useMisuseDetection';
import GroupEditor from './GroupEditor';
import CountInterface from './CountInterface';
import SessionsCalendar from './SessionsCalendar';
import Toast from './Toast';
import ArchiveView from './ArchiveView';
import AutoScheduleCreator from '../../../scheduler-files/AutoScheduleCreator.jsx';
import DatabaseColumn from './cards/DatabaseColumn';
import { getDatabaseColumnPreset, getDatabaseBrowserPreset } from './cards/ItemCardConfig';
import {
  queryCountSessionsActive,
  queryCountSessionsCompleted,
  queryInventoryItems,
  queryCountTemplates,
  queryCountSchedules,
  directQuery
} from '../utils/directFetch';
import { formatUserDisplayName, formatUserCompact, getCurrentUser as getStoredUser, shortId, isStoreOwnedBySuperAdmin, SUPER_ADMIN_USER_IDS, COUNTERTOP_APP_CREATOR_LABEL } from '../utils/userDisplay';
import { getTemplateColor, pickNextTemplateColor } from '../utils/templateColor';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { optionSummaryLine } from '../utils/purchaseOptions';

const CountSessionManager = ({ onCancel, initialStep = 'create-session', mode = 'normal' }) => {
  const [misuseStoreId] = useState(() => {
    const s = localStorage.getItem('selectedStoreId');
    return s ? parseInt(s, 10) : null;
  });
  const { runDetection: runMisuseDetection, modalElement: misuseModalElement } = useMisuseDetection(misuseStoreId);
  // Page-open detection guard: only run on the templates page (initialStep='dashboard'),
  // NOT on count-sessions.html or any other page that mounts this component.
  // Also guards against double-fire on reloads after a save.
  const pageOpenDetectionRanRef = useRef(false);
  const isTemplatesPage = initialStep === 'dashboard';

  const [step, setStep] = useState(initialStep); // 'dashboard', 'create-template', 'location-template-creator', 'customize-template', 'create-session', 'count', 'report'
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [editingGroupForEditor, setEditingGroupForEditor] = useState(null);
  const [highlightTemplateId, setHighlightTemplateId] = useState(null);
  const queryParamHandledRef = useRef(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [countSessions, setCountSessions] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplatesForSession, setSelectedTemplatesForSession] = useState([]);
  const [userTimezone, setUserTimezone] = useState('America/Los_Angeles'); // Default to PST
  const [currentUser, setCurrentUser] = useState(null);

  // Item filtering state for session creation
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemCategoryFilter, setItemCategoryFilter] = useState('');
  const [itemVendorFilter, setItemVendorFilter] = useState('');
  const [itemLocationFilter, setItemLocationFilter] = useState('');
  const [templatesDisplayCount, setTemplatesDisplayCount] = useState(25);
  // Purchase-option counts per inventory item ("N active purchase options"
  // notes under template item descriptions). Loaded non-fatally, batched for
  // the visible items, and cached by item id for the lifetime of the mount.
  const [purchaseOptionsByItemId, setPurchaseOptionsByItemId] = useState({});
  const purchaseOptionsRequestedRef = useRef(new Set());
  const [showCalendarView, setShowCalendarView] = useState(true);
  const [databaseFilteredItems, setDatabaseFilteredItems] = useState([]);
  const [databaseCheckedItems, setDatabaseCheckedItems] = useState([]);

  // Template management state
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showArchiveView, setShowArchiveView] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [templatesToArchive, setTemplatesToArchive] = useState([]);

  // Template delete dialog branching: null | 'checking' | 'no-history' | 'has-history'
  const [templateDeleteDialogMode, setTemplateDeleteDialogMode] = useState(null);
  const [checkingTemplateHistory, setCheckingTemplateHistory] = useState(false);

  // Database browser state
  const [showDatabaseBrowser, setShowDatabaseBrowser] = useState(false);
  const [templateSortBy, setTemplateSortBy] = useState('created_at'); // 'name', 'created_at', 'last_used'
  const [templateSortDropdownOpen, setTemplateSortDropdownOpen] = useState(false);

  // Template filtering state
  const [templateFilters, setTemplateFilters] = useState({
    status: 'all', // 'all', 'active', 'archived'
    orderType: 'all' // 'all', 'order', 'count-only'
  });
  const [templateFilterDropdownOpen, setTemplateFilterDropdownOpen] = useState(false);

  // Small gray "N active purchase options" note for a template item, or null
  // when the item has no (loaded) active options.
  const renderPurchaseOptionSummary = (itemId) => {
    if (itemId === null || itemId === undefined) return null;
    const options = purchaseOptionsByItemId?.[itemId];
    if (!(options || []).some((o) => o?.status === 'active')) return null;
    return (
      <div className="text-muted" style={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
        {optionSummaryLine(options)}
      </div>
    );
  };

  // Batch-load purchase-option counts for the items currently visible in
  // template cards (first 8 per card) and in the template editor's selected
  // list. Display-only: failures are swallowed and the notes simply stay
  // hidden (e.g. purchase_options table not deployed yet). Never touches
  // count_template_items membership.
  useEffect(() => {
    const storeId = parseInt(localStorage.getItem('selectedStoreId'), 10);
    if (!storeId) return undefined;
    const ids = [];
    (templates || []).slice(0, templatesDisplayCount).forEach((template) => {
      (Array.isArray(template.items) ? template.items : []).slice(0, 8).forEach((item) => {
        // Template cards store either bare inventory ids or item objects.
        const invId = (item !== null && typeof item === 'object') ? item.id : item;
        if (invId !== null && invId !== undefined) ids.push(invId);
      });
    });
    (selectedItems || []).forEach((item) => {
      if (item?.id !== null && item?.id !== undefined) ids.push(item.id);
    });
    const missing = [];
    ids.forEach((invId) => {
      const key = String(invId);
      if (!purchaseOptionsRequestedRef.current.has(key)) {
        purchaseOptionsRequestedRef.current.add(key);
        missing.push(invId);
      }
    });
    if (missing.length === 0) return undefined;
    let cancelled = false;
    fetchOptionsForItems(storeId, missing)
      .then((grouped) => {
        if (cancelled) return;
        setPurchaseOptionsByItemId((prev) => ({ ...prev, ...(grouped || {}) }));
      })
      .catch((err) => {
        console.warn('[purchase-options] template item summary load failed (non-fatal):', err?.message);
      });
    return () => { cancelled = true; };
  }, [templates, templatesDisplayCount, selectedItems]);
  
  // Session management state
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  const [sessionSortBy, setSessionSortBy] = useState('date'); // 'name', 'date', 'status'
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all'); // 'all', 'pending', 'completed'
  
  // Edit mode state
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Tab state (only templates now)
  const [activeTab, setActiveTab] = useState('templates'); // only 'templates'
  const [schedules, setSchedules] = useState([]); // Keep for backward compatibility

  // Template creation state (no time fields)
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    category: 'custom'
  });

  // Count session state (with time fields)
  const [countSession, setCountSession] = useState({
    name: '',
    session_date: new Date().toISOString().split('T')[0],
    created_time: getCurrentLocalTime(),
    count_commence_time: getCurrentLocalTime(),
    notes: ''
  });

  const [sessionOrderMode, setSessionOrderMode] = useState(false);

  // Toast helper function
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const renderChronologicalListView = () => {
    // Combine and sort all sessions by date
    const allSessions = [...countSessions, ...completedSessions].sort((a, b) => {
      return new Date(a.session_date) - new Date(b.session_date);
    });

    return (
      <div>
        {/* Session Management Controls */}
        <div className="mb-3 p-3" style={{ backgroundColor: '#F8F9FA', borderRadius: '8px', border: '1px solid #DEE2E6' }}>
          <div className="d-flex justify-content-end align-items-center">
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle"
                type="button"
                id="sessionSortDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="feather icon-sort me-1"></i>
                Sort: {sessionSortBy === 'name' ? 'Name' : sessionSortBy === 'date' ? 'Date' : 'Status'}
              </button>
              <ul className="dropdown-menu" aria-labelledby="sessionSortDropdown">
                <li>
                  <button 
                    className={`dropdown-item ${sessionSortBy === 'name' ? 'active' : ''}`}
                    onClick={() => setSessionSortBy('name')}
                  >
                    <i className="feather icon-type me-2"></i>
                    Sort by Name
                  </button>
                </li>
                <li>
                  <button 
                    className={`dropdown-item ${sessionSortBy === 'date' ? 'active' : ''}`}
                    onClick={() => setSessionSortBy('date')}
                  >
                    <i className="feather icon-calendar me-2"></i>
                    Sort by Date
                  </button>
                </li>
                <li>
                  <button 
                    className={`dropdown-item ${sessionSortBy === 'status' ? 'active' : ''}`}
                    onClick={() => setSessionSortBy('status')}
                  >
                    <i className="feather icon-activity me-2"></i>
                    Sort by Status
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-3">
          <div className="row g-2 justify-content-center">
            <div className="col-md-7">
              <div className="input-group">
                <span className="input-group-text" style={{ backgroundColor: '#F5F3F0', borderColor: '#8A7A6B' }}>
                  <i className="feather icon-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search sessions..."
                  value={sessionSearchTerm}
                  onChange={(e) => setSessionSearchTerm(e.target.value)}
                  onInput={(e) => setSessionSearchTerm(e.target.value)}
                  style={{ borderColor: '#8A7A6B' }}
                  autoComplete="off"
                  data-lpignore="true"
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={sessionStatusFilter}
                onChange={(e) => setSessionStatusFilter(e.target.value)}
                style={{ borderColor: '#8A7A6B' }}
              >
                <option value="all">All Sessions</option>
                <option value="pending">Pending (Draft/In Progress)</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sessions List */}
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {allSessions.length === 0 ? (
            <div className="text-center p-4" style={{ color: '#8A7A6B' }}>
              <i className="feather icon-calendar" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
              <p>No count sessions scheduled.</p>
              <button
                className="btn btn-sm"
                onClick={() => setStep('create-session')}
                style={{
                  backgroundColor: '#8A7A6B',
                  borderColor: '#8A7A6B',
                  color: 'white'
                }}
              >
                <i className="feather icon-plus me-1"></i>
                Create Your First Session
              </button>
            </div>
          ) : (
            <div className="row">
              {allSessions
                .filter(session => {
                  // Search filter
                  const matchesSearch = !sessionSearchTerm || 
                    session.name?.toLowerCase().includes(sessionSearchTerm.toLowerCase()) ||
                    session.status?.toLowerCase().includes(sessionSearchTerm.toLowerCase());
                  
                  // Status filter
                  const matchesStatus = sessionStatusFilter === 'all' ||
                    (sessionStatusFilter === 'pending' && (session.status === 'draft' || session.status === 'in_progress' || session.status === 'paused')) ||
                    (sessionStatusFilter === 'completed' && session.status === 'completed');
                  
                  return matchesSearch && matchesStatus;
                })
                .sort((a, b) => {
                  switch (sessionSortBy) {
                    case 'name':
                      return (a.name || '').localeCompare(b.name || '');
                    case 'date':
                      return new Date(a.session_date) - new Date(b.session_date);
                    case 'status':
                      return (a.status || '').localeCompare(b.status || '');
                    default:
                      return new Date(a.session_date) - new Date(b.session_date);
                  }
                })
                .map(session => {
                  const sessionDate = new Date(session.session_date);
                  const isToday = sessionDate.toDateString() === new Date().toDateString();
                  const isPast = sessionDate < new Date() && !isToday;
                  const isFuture = sessionDate > new Date();

                  let statusColor = '#8A7A6B';
                  let statusBg = '#F5F3F0';
                  let statusText = session.status;

                  switch (session.status) {
                    case 'draft':
                      statusColor = '#6C757D';
                      statusBg = '#F8F9FA';
                      statusText = 'Draft';
                      break;
                    case 'in_progress':
                      statusColor = '#8A7A6B';
                      statusBg = '#F5F3F0';
                      statusText = 'In Progress';
                      break;
                    case 'paused':
                      statusColor = '#FB8C00';
                      statusBg = '#FFF3E0';
                      statusText = 'Saved';
                      break;
                    case 'completed':
                      statusColor = '#5A4A42';
                      statusBg = '#E8F5E8';
                      statusText = 'Completed';
                      break;
                  }

                  return (
                    <div key={session.id} className="col-md-6 col-lg-4 mb-3">
                    <div
                      className="card position-relative"
                      style={{
                        border: '1px solid #8A7A6B',
                        borderTop: '2px solid #5A4A42',
                        borderBottom: '2px solid #5A4A42',
                        cursor: session.status !== 'completed' ? 'pointer' : 'default',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(90, 74, 66, 0.1)',
                        transition: 'all 0.2s ease',
                        height: '220px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={() => {
                        if (session.status !== 'completed') {
                          console.log('Opening session:', session);
                          if (session.status === 'draft' || session.status === 'in_progress' || session.status === 'paused') {
                            window.location.href = 'count.html';
                          }
                        }
                      }}
                      title={`Count Session: ${session.name || `Session ${session.id}`}
Date: ${sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
Time: ${session.count_commence_time || 'Not set'}
Progress: ${(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items` : `${c} items`; })()}
Status: ${session.status}${session.notes ? `
Notes: ${session.notes}` : ''}`}
                    >
                      <div className="card-body p-3 d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <h6 className="mb-0" style={{ color: '#5A4A42', fontWeight: '600', fontSize: '0.9rem' }}>
                              {session.name || `Count Session ${session.id}`}
                            </h6>
                          </div>
                        </div>
                        
                        <div className="flex-grow-1 text-muted" style={{ fontSize: '0.8rem' }}>
                          <p className="mb-1">
                            <i className="feather icon-calendar me-1"></i>
                            {sessionDate.toLocaleDateString('en-US', { 
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {isToday && <span className="badge bg-warning ms-2">Today</span>}
                            {isPast && <span className="badge bg-secondary ms-2">Past</span>}
                          </p>
                          
                          {session.count_commence_time && (
                            <p className="mb-1">
                              <i className="feather icon-clock me-1"></i>
                              {session.count_commence_time}
                            </p>
                          )}
                          
                          {(session.total_items || session.items_counted) ? (
                            <p className="mb-1">
                              <i className="feather icon-package me-1"></i>
                              {(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items` : `${c} items`; })()}
                            </p>
                          ) : null}
                        </div>

                        <div className="mt-auto">
                          {session.status !== 'completed' ? (
                            <div className="d-flex gap-1">
                              {/* Edit Button - Only show for draft status with no counted items */}
                              {(session.status === 'draft' && (session.items_counted || 0) === 0) && (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  style={{
                                    fontSize: '0.7rem',
                                    padding: '0.25rem 0.5rem',
                                    flex: '0 0 auto'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditSession(session);
                                  }}
                                  title="Edit session details and items"
                                >
                                  <i className="feather icon-edit-2"></i>
                                </button>
                              )}
                              
                              {/* Continue/Start Button */}
                            <button
                              className="btn btn-sm w-100"
                              style={{
                                backgroundColor: '#8A7A6B',
                                borderColor: '#8A7A6B',
                                color: 'white',
                                fontSize: '0.75rem'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                startOrContinueSession(session);
                              }}
                            >
                              <i className="feather icon-play me-1"></i>
                              {session.status === 'draft' ? 'Start' : 'Resume'}
                            </button>
                            </div>
                          ) : (
                            <div className="text-center">
                              <i className="fa fa-check" style={{ fontSize: '25px', color: '#8A7A6B' }}></i>
                              <p style={{ color: '#8A7A6B', marginTop: '5px', marginBottom: '6px' }}>Done</p>
                              <button
                                className="btn btn-sm"
                                style={{ fontSize: '0.65rem', padding: '2px 8px', backgroundColor: 'transparent', border: '1px solid #C9B9A8', color: '#8A7A6B', borderRadius: '4px' }}
                                title="Mark this count as invalid — excludes it from reports while preserving the audit record. Voided sessions remain visible in the archive."
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVoidSession(session);
                                }}
                              >
                                ⊘ Void
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleVoidSession = async (session) => {
    const confirmed = window.confirm(
      `Void "${session.name || 'Unnamed count'}"?\n\nVoiding marks this count as invalid and excludes it from reports and inventory totals, but keeps the record visible in the archive for auditing.\n\nThis cannot be automatically undone.`
    );
    if (!confirmed) return;
    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ voided_at: new Date().toISOString(), is_archived: true })
        .eq('id', session.id);
      if (error) throw error;
      loadCountSessions();
    } catch (err) {
      console.error('Error voiding session:', err);
      alert('Error voiding session: ' + err.message);
    }
  };

  const startOrContinueSession = async (session) => {
    if (session.status === 'draft') {
      // Update the status to 'in_progress'
      try {
        const { error } = await supabase
          .from('count_sessions')
          .update({ status: 'in_progress' })
          .eq('id', session.id);

        if (error) {
          console.error('Error updating session status:', error);
          return;
        }
      } catch (error) {
        console.error('Error updating session status:', error);
        return;
      }
    }
    // Navigate to the dedicated count session page
    window.location.href = `count-session.html?session=${session.id}`;
  };

  const handleEditSession = async (session) => {
    try {
      setLoading(true);
      console.log('🔧 Redirecting to new count creation interface for editing:', session);

      // Store the session data for editing in the new interface
      localStorage.setItem('editSessionData', JSON.stringify({
        id: session.id,
        name: session.name,
        session_date: session.session_date,
        created_time: session.created_time,
        count_commence_time: session.count_commence_time,
        notes: session.notes,
        template_id: session.template_id,
        total_items: session.total_items,
        items_counted: session.items_counted,
        order_calculations_enabled: session.order_calculations_enabled || false,
        direct_order_mode: session.direct_order_mode || false,
        vendor_id: session.vendor_id || null,
        count_purpose: session.count_purpose || null,
        ordering_cycle_config: session.ordering_cycle_config || null
      }));

      // Redirect to new count creation interface with session name and edit flag
      const sessionName = encodeURIComponent(session.name || 'Unnamed Session');
      window.location.href = `/count.html?view=count-creation&sessionName=${sessionName}&edit=true`;

    } catch (error) {
      console.error('Error setting up edit session:', error);
      alert('Error loading session for editing: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate automatic session name - no user input required
  const generateSessionName = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      timeZone: userTimezone,
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });

    // Get template name from first selected template
    const templateName = selectedTemplatesForSession.length > 0
      ? selectedTemplatesForSession[0].name
      : 'Count';

    // Use compact display name (First L.) so sessions are human-readable
    const userIdentifier = currentUser?.compactName || currentUser?.displayName || currentUser?.email || 'User';

    return `${dateStr} - ${templateName} - ${userIdentifier}`;
  };

  // Get current local time in user-friendly format
  function getCurrentLocalTime() {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    });
  }

  // Get current date in user's timezone
  function getCurrentLocalDate() {
    const now = new Date();
    return now.toLocaleDateString('en-CA', { // ISO format YYYY-MM-DD
      timeZone: userTimezone
    });
  }

  // Detect user's timezone
  function detectUserTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('Detected timezone:', timezone);
      return timezone;
    } catch (error) {
      console.log('Could not detect timezone, using PST default');
      return 'America/Los_Angeles'; // Default to PST
    }
  }

  // Convert 12-hour time to 24-hour for HTML time input
  function convertTo24Hour(time12) {
    if (!time12) return '';

    const [time, period] = time12.split(' ');
    let [hours, minutes] = time.split(':');

    if (period === 'PM' && hours !== '12') {
      hours = parseInt(hours) + 12;
    } else if (period === 'AM' && hours === '12') {
      hours = '00';
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }

  // Convert 24-hour time to 12-hour with AM/PM
  function convertTo12Hour(time24) {
    if (!time24) return '';

    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    return `${displayHour}:${minutes} ${period}`;
  }

  // Initialize timezone and load data
  useEffect(() => {
    // Always use templates tab (schedules tab removed)
    setActiveTab('templates');

    const detectedTimezone = detectUserTimezone();
    setUserTimezone(detectedTimezone);

    // Update session with current time in detected timezone
    setCountSession(prev => ({
      ...prev,
      session_date: getCurrentLocalDate(),
      created_time: getCurrentLocalTime(),
      count_commence_time: getCurrentLocalTime()
    }));

    loadCurrentUser();
    loadInventoryData();
    loadTemplates();
    loadCountSessions();
    loadSchedules();
    loadStoreLocations();

    // Initialize Bootstrap tooltips
    const initTooltips = () => {
      if (typeof window !== 'undefined' && window.bootstrap) {
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
          return new window.bootstrap.Tooltip(tooltipTriggerEl, {
            html: true,
            delay: { show: 500, hide: 100 }
          });
        });
      }
    };

    // Initialize tooltips after a short delay to ensure DOM is ready
    setTimeout(initTooltips, 100);

    // Check for editSessionId from localStorage (redirected from CountInterface)
    const checkForEdit = async () => {
      const editSessionId = localStorage.getItem('editSessionId');
      if (editSessionId) {
        console.log('🔄 Auto-loading session for editing:', editSessionId);
        localStorage.removeItem('editSessionId'); // Clean up
        
        try {
          // Force reload sessions first
          await loadCountSessions();
          
          // Wait a bit for state to update
          setTimeout(() => {
            // Try to find the session
            const sessionToEdit = countSessions.find(s => 
              s.id.toString() === editSessionId || s.id === parseInt(editSessionId)
            );
            
            if (sessionToEdit) {
              console.log('✅ Found session for editing:', sessionToEdit);
              handleEditSession(sessionToEdit);
            } else {
              console.error('❌ Session not found for editing:', editSessionId);
              console.log('Available sessions:', countSessions.map(s => ({id: s.id, name: s.name})));
              
              // Try to load the session directly from database
              supabase
                .from('count_sessions')
                .select('*')
                .eq('id', editSessionId)
                .single()
                .then(({ data: session, error }) => {
                  if (error || !session) {
                    alert('Could not find the session to edit. Please try again.');
                    return;
                  }
                  console.log('📥 Loaded session directly from database:', session);
                  handleEditSession(session);
                });
            }
          }, 200);
          
        } catch (error) {
          console.error('Error loading sessions for edit:', error);
          alert('Error loading session for editing: ' + error.message);
        }
      }
    };
    
    // Run the check after a delay to ensure everything is initialized
    setTimeout(checkForEdit, 1000);
  }, []);

  // Update times when timezone changes
  useEffect(() => {
    setCountSession(prev => ({
      ...prev,
      created_time: getCurrentLocalTime(),
      count_commence_time: getCurrentLocalTime()
    }));
  }, [userTimezone]);

  // Re-load inventory data when another component reports a vendor change so
  // item cards immediately display the updated vendor name without a page reload.
  useEffect(() => {
    const handleVendorChange = () => { loadInventoryData(); };
    document.addEventListener('vendorDataChanged', handleVendorChange);
    return () => document.removeEventListener('vendorDataChanged', handleVendorChange);
  }, []);

  // Handle ?edit=<templateId> and ?highlight=<templateId> query params from prep cards.
  // Runs once after templates are first loaded; cleans the URL afterwards.
  useEffect(() => {
    if (templates.length === 0 || queryParamHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);

    const editId = params.get('edit');
    const highlightId = params.get('highlight');
    const targetId = editId || highlightId;
    if (!targetId) return;

    // Try direct count_templates id match first.
    let tmpl = templates.find((t) => String(t.id) === String(targetId));

    // Fallback: prep cards pass count_sessions.id (where ordering_cycle_config
    // lives). Templates here come from count_templates. Bridge by name.
    const resolve = async () => {
      if (!tmpl) {
        try {
          const { data: sessRow } = await supabase
            .from('count_sessions')
            .select('name')
            .eq('id', targetId)
            .eq('is_template', true)
            .maybeSingle();
          if (sessRow?.name) {
            tmpl = templates.find((t) => t.name === sessRow.name);
          }
        } catch (_) {}
      }
      if (!tmpl) return;
      queryParamHandledRef.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      if (editId) {
        setEditingGroupForEditor(tmpl);
        setStep('location-template-creator');
      } else {
        setHighlightTemplateId(String(tmpl.id));
      }
    };
    resolve();
  }, [templates]);

  // Scroll the highlighted template card into view and clear the highlight after a delay.
  useEffect(() => {
    if (!highlightTemplateId) return;
    const el = document.querySelector(`[data-template-id="${highlightTemplateId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timer = setTimeout(() => setHighlightTemplateId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightTemplateId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (templateSortDropdownOpen && !event.target.closest('.template-sort-dropdown')) {
        setTemplateSortDropdownOpen(false);
      }
      if (templateFilterDropdownOpen && !event.target.closest('.template-filter-dropdown')) {
        setTemplateFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [templateSortDropdownOpen, templateFilterDropdownOpen]);

  // Filter templates based on selected filters
  const getFilteredTemplates = (templatesArray) => {
    return templatesArray.filter(template => {
      // Status filter (active vs archived)
      if (templateFilters.status === 'active' && template.is_active !== true) {
        return false;
      }
      if (templateFilters.status === 'archived' && template.is_active !== false) {
        return false;
      }

      // Order type filter
      if (templateFilters.orderType === 'order' && !template.order_calculations_enabled) {
        return false;
      }
      if (templateFilters.orderType === 'count-only' && template.order_calculations_enabled) {
        return false;
      }

      return true;
    });
  };

  // Get current filter description for display
  const getFilterDescription = () => {
    const parts = [];
    if (templateFilters.status !== 'all') {
      parts.push(templateFilters.status === 'active' ? 'Active' : 'Archived');
    }
    if (templateFilters.orderType !== 'all') {
      parts.push(templateFilters.orderType === 'order' ? 'Order & Count' : 'Count Only');
    }
    return parts.length > 0 ? parts.join(' + ') : 'All';
  };

  // Automatically generate session name when templates change (not on initial load)
  useEffect(() => {
    if (currentUser && selectedTemplatesForSession.length > 0 && countSession.name === '') {
      const autoName = generateSessionName();
      console.log('🎯 Auto-generating session name from templates:', autoName);
      setCountSession(prev => ({
        ...prev,
        name: autoName
      }));
    }
  }, [selectedTemplatesForSession, currentUser, userTimezone]);

  const loadCurrentUser = async () => {
    try {
      const user = getStoredUser();
      if (user) {
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadInventoryData = async () => {
    setLoading(true);
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('No user or store found in localStorage');
        setLoading(false);
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Loading inventory for user:', user.email, 'store:', storeId);

      console.log('🔍 [DB-CALL-8] Starting inventory_items query for CountSessionManager, store:', storeId);

      const { data, error } = await queryInventoryItems(storeId);

      if (error) {
        console.error('Error loading inventory:', error);
        return;
      }

      setInventoryData(data || []);

      // Make inventory data available globally for ItemDatabaseBrowser
      window.globalInventoryData = data || [];
      console.log('✅ [CountSessionManager] Made inventory data available globally:', window.globalInventoryData.length, 'items');
      console.log('🔍 [CountSessionManager] Sample inventory item:', window.globalInventoryData[0]);

      // Dispatch event to update ItemDatabaseBrowser with new inventory data
      console.log('🔄 [CountSessionManager] Dispatching inventory data update event');
      const event = new CustomEvent('updateItemDatabaseBrowserInventory', {
        detail: {
          inventoryData: window.globalInventoryData,
          currentItems: [] // Will be updated by the component
        }
      });
      document.dispatchEvent(event);
    } catch (error) {
      console.error('Error loading inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('No user or store found in localStorage');
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Loading templates for user:', user.email, 'store:', storeId);

      // Load templates (exclude archived and deleted)
      console.log('🔍 [DB-CALL-7] Starting count_templates query for CountSessionManager, store:', storeId);

      const { data: templatesData, error: templatesError } = await queryCountTemplates(storeId);

      if (templatesError) {
        console.error('Error loading templates:', templatesError);
        return;
      }

      // Resolve creator names for all templates in a single batched query
      const creatorIds = [...new Set((templatesData || []).map(t => t.created_by).filter(Boolean))];
      const creatorNameMap = {};
      if (creatorIds.length > 0) {
        try {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, display_name, first_name, last_name, email')
            .in('id', creatorIds);
          (profiles || []).forEach(p => {
            creatorNameMap[p.id] = formatUserCompact(p) || p.email || shortId(p.id);
          });
        } catch (_) {
          // Non-critical; fall back to current-user check below
        }
      }

      // Templates the super admin created in a store they don't own read as
      // "Countertop App" rather than the admin's name.
      const adminOwnsStore = await isStoreOwnedBySuperAdmin(supabase, storeId);
      if (!adminOwnsStore) {
        SUPER_ADMIN_USER_IDS.forEach((id) => { creatorNameMap[id] = COUNTERTOP_APP_CREATOR_LABEL; });
      }

      // Load template items for each template (handle both new and legacy storage)
      const templatesWithItems = await Promise.all(
        (templatesData || []).map(async (template) => {
          // First try the new count_template_items table - USE DIRECT FETCH
          const { data: templateItems, error: itemsError } = await directQuery('count_template_items', {
            select: 'inventory_item_id',
            eq: {
              template_id: template.id,
              include_in_count: true
            },
            order: { column: 'template_sort_position', ascending: true }
          });

          if (itemsError) {
            console.error('Error loading template items for template', template.id, itemsError);
          }

          let itemIds = [];

          if (templateItems && templateItems.length > 0) {
            itemIds = templateItems.map(item => item.inventory_item_id);
          } else if (template.items && Array.isArray(template.items) && template.items.length > 0) {
            itemIds = template.items;
          }

          // Resolve creator display name
          let creatorName = null;
          if (template.created_by) {
            if (creatorNameMap[template.created_by]) {
              creatorName = creatorNameMap[template.created_by];
            } else {
              // Will be matched against currentUser at render time
              creatorName = null;
            }
          }

          return { ...template, items: itemIds, creatorName };
        })
      );

      setTemplates(templatesWithItems);
      // Page-open detection: only fire on the templates page (initialStep='dashboard'),
      // and only once per mount to avoid double-prompts after save-triggered reloads.
      if (isTemplatesPage && !pageOpenDetectionRanRef.current) {
        pageOpenDetectionRanRef.current = true;
        runMisuseDetection();
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Template Management Functions
  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  const selectAllTemplates = () => {
    const currentTemplateIds = templates.map(t => t.id);
    setSelectedTemplates(currentTemplateIds);
  };

  const clearTemplateSelection = () => {
    setSelectedTemplates([]);
  };

  const handleArchiveTemplates = async () => {
    if (selectedTemplates.length === 0) return;
    
    try {
      const { error } = await supabase
        .from('count_templates')
        .update({ 
          is_archived: true,
          archived_at: new Date().toISOString()
        })
        .in('id', selectedTemplates);

      if (error) {
        console.error('Error archiving templates:', error);
        alert('Error archiving templates: ' + error.message);
        return;
      }

      console.log(`✅ Archived ${selectedTemplates.length} templates`);
      setSelectedTemplates([]);
      setShowArchiveModal(false);
      loadTemplates(); // Reload to remove archived templates
    } catch (error) {
      console.error('Error archiving templates:', error);
      alert('Error archiving templates: ' + error.message);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!templateId) return;
    
    try {
      const storeId = parseInt(localStorage.getItem('selectedStoreId'));
      const { count: completedCount, error: historyCheckError } = await supabase
        .from('count_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('template_id', templateId)
        .eq('status', 'completed');

      if (historyCheckError) {
        alert('Could not verify count history before deleting. Please try again.');
        return;
      }

      if ((completedCount || 0) > 0) {
        alert('This template has completed count history and cannot be deleted. Archive it instead to stop using it while keeping the history.');
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setTemplateDeleteDialogMode(null);
        return;
      }

      const { error: unlinkError } = await supabase
        .from('count_sessions')
        .update({ template_id: null })
        .eq('store_id', storeId)
        .eq('template_id', templateId)
        .neq('status', 'completed');

      if (unlinkError) {
        console.error('Error unlinking draft sessions from template:', unlinkError);
        alert('Could not clean up draft sessions before deleting. Please try again.');
        return;
      }

      const { error: itemsError } = await supabase
        .from('count_template_items')
        .delete()
        .eq('template_id', templateId);

      if (itemsError) {
        console.error('Error deleting template items:', itemsError);
        alert('Could not remove template items before deleting. Please try again.');
        return;
      }

      const { error } = await supabase
        .from('count_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        console.error('Error deleting template:', error);
        alert('Error deleting template: ' + error.message);
        return;
      }

      console.log(`✅ Permanently deleted template ${templateId}`);
      setTemplateToDelete(null);
      setTemplateDeleteDialogMode(null);
      setShowDeleteModal(false);
      setSelectedTemplates(prev => prev.filter(id => id !== templateId));
      loadTemplates(); // Reload to remove deleted template
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template: ' + error.message);
    }
  };

  /**
   * Check whether a template has completed count history, then open the
   * appropriate dialog:
   *   - has history  → archive-only dialog (deletion blocked)
   *   - no history   → standard delete confirmation
   */
  const handleRequestDeleteTemplate = async (templateId) => {
    if (!templateId) return;
    setTemplateToDelete(templateId);
    setTemplateDeleteDialogMode(null);
    setCheckingTemplateHistory(true);
    setShowDeleteModal(true);
    try {
      const storeId = parseInt(localStorage.getItem('selectedStoreId'));
      const { count, error } = await supabase
        .from('count_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('template_id', templateId)
        .eq('status', 'completed');
      if (error) throw error;
      setTemplateDeleteDialogMode((count || 0) > 0 ? 'has-history' : 'no-history');
    } catch (err) {
      console.error('Error checking template history:', err);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      setTemplateDeleteDialogMode(null);
      alert('Could not verify count history for this template. Please try again.');
    } finally {
      setCheckingTemplateHistory(false);
    }
  };

  /** Archive a single template by ID (used from the delete-blocked dialog). */
  const handleArchiveSingleTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const { error } = await supabase
        .from('count_templates')
        .update({ is_archived: true, archived_at: new Date().toISOString() })
        .eq('id', templateId);
      if (error) throw error;
      console.log(`✅ Archived template ${templateId}`);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      setTemplateDeleteDialogMode(null);
      setSelectedTemplates(prev => prev.filter(id => id !== templateId));
      loadTemplates();
      showToastMessage('Template archived successfully');
    } catch (err) {
      console.error('Error archiving template:', err);
      alert('Error archiving template: ' + err.message);
    }
  };

  const loadArchivedTemplates = async () => {
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('No user or store found in localStorage');
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Loading archived templates for user:', user.email, 'store:', storeId);

      // Load archived templates
      const { data: templatesData, error } = await supabase
        .from('count_templates')
        .select('*')
        .eq('store_id', storeId)
        .eq('is_active', true)
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });

      if (error) {
        console.error('Error loading archived templates:', error);
        return;
      }

      // Load template items for each archived template
      const templatesWithItems = await Promise.all(
        (templatesData || []).map(async (template) => {
          const { data: templateItems } = await directQuery('count_template_items', {
            select: 'inventory_item_id',
            eq: {
              template_id: template.id,
              include_in_count: true
            }
          });

          const itemIds = templateItems?.map(item => item.inventory_item_id) || [];
          return { ...template, items: itemIds };
        })
      );

      return templatesWithItems;
    } catch (error) {
      console.error('Error loading archived templates:', error);
      return [];
    }
  };

  const unarchiveTemplate = async (templateId) => {
    try {
      const { error } = await supabase
        .from('count_templates')
        .update({ 
          is_archived: false,
          archived_at: null
        })
        .eq('id', templateId);

      if (error) {
        console.error('Error unarchiving template:', error);
        alert('Error unarchiving template: ' + error.message);
        return;
      }

      console.log(`✅ Unarchived template ${templateId}`);
      loadTemplates(); // Reload main templates
    } catch (error) {
      console.error('Error unarchiving template:', error);
      alert('Error unarchiving template: ' + error.message);
    }
  };

  const loadCountSessions = async () => {
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('No user or store found in localStorage');
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Loading sessions for user:', user.email, 'store:', storeId);

      // Load active sessions (draft and in_progress)
      console.log('🔍 [DB-CALL-4] Starting count_sessions (active) query for CountSessionManager, store:', storeId);

      const { data: activeSessions, error: activeError } = await queryCountSessionsActive(storeId);

      if (activeError) {
        console.error('❌ [DB-CALL-4] Error loading active count sessions:', activeError);
        return;
      }

      // Load completed sessions (last 10)
      console.log('🔍 [DB-CALL-5] Starting count_sessions (completed) query for CountSessionManager, store:', storeId);

      const { data: completedSessionsData, error: completedError } = await queryCountSessionsCompleted(storeId, 10);

      if (completedError) {
        console.error('Error loading completed count sessions:', completedError);
        return;
      }

      setCountSessions(activeSessions || []);
      setCompletedSessions(completedSessionsData || []);
    } catch (error) {
      console.error('Error loading count sessions:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        console.error('No user or store found in localStorage');
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Loading schedules for user:', user.email, 'store:', storeId);

      // Load schedules with template names
      console.log('🔍 [DB-CALL-6] Starting count_schedules query for CountSessionManager, store:', storeId);

      const { data: schedulesData, error: schedulesError } = await queryCountSchedules(storeId);

      // Note: Direct fetch doesn't support joins, so template names will need to be loaded separately if needed

      if (schedulesError) {
        console.error('Error loading schedules:', schedulesError);
        return;
      }

      setSchedules(schedulesData || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const loadStoreLocations = async () => {
    // Implementation of loadStoreLocations function
  };

  // Sort and filter templates based on selected criteria
  const getSortedTemplates = (templatesArray) => {
    // First apply filters
    const filteredTemplates = getFilteredTemplates(templatesArray);

    // Then sort the filtered results
    return [...filteredTemplates].sort((a, b) => {
      switch (templateSortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created_at':
          // Default sort: newest first
          return new Date(b.created_at) - new Date(a.created_at);
        case 'last_used':
          // Sort by last used date, with never used items at the end
          const aLastUsed = a.last_used_at ? new Date(a.last_used_at) : new Date(0);
          const bLastUsed = b.last_used_at ? new Date(b.last_used_at) : new Date(0);
          return bLastUsed - aLastUsed;
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
  };

  const getUniqueValues = (field) => {
    const values = inventoryData
      .map(item => item[field])
      .filter(value => value && value.trim())
      .map(value => value.trim());
    return [...new Set(values)].sort();
  };

  // Get filtered items for session creation (excluding items already in templates)
  const getFilteredItems = () => {
    // Get all item IDs that are already included in selected templates
    const templateItemIds = selectedTemplatesForSession.reduce((acc, template) => {
      if (Array.isArray(template.items)) {
        return [...acc, ...template.items];
      }
      return acc;
    }, []);

    // Filter inventory data
    let filtered = inventoryData.filter(item => {
      // Exclude items already in selected templates
      if (templateItemIds.includes(item.id)) return false;

      // Exclude items already individually selected
      if (selectedItems.find(si => si.id === item.id)) return false;

      return true;
    });

    // Apply search filter
    if (itemSearchTerm) {
      filtered = filtered.filter(item =>
        item.description?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.brand?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.supc?.toLowerCase().includes(itemSearchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (itemCategoryFilter) {
      filtered = filtered.filter(item =>
        item.category?.toLowerCase() === itemCategoryFilter.toLowerCase()
      );
    }

    // Apply vendor filter
    if (itemVendorFilter) {
      filtered = filtered.filter(item =>
        item.brand?.toLowerCase() === itemVendorFilter.toLowerCase()
      );
    }

    // Apply location filter
    if (itemLocationFilter) {
      filtered = filtered.filter(item =>
        item.location?.toLowerCase() === itemLocationFilter.toLowerCase()
      );
    }

    return filtered;
  };

  const handleBulkSelection = (type, value) => {
    let itemsToAdd = [];
    
    switch (type) {
      case 'category':
        itemsToAdd = inventoryData.filter(item => 
          item.category?.toLowerCase() === value.toLowerCase()
        );
        break;
      case 'location':
        itemsToAdd = inventoryData.filter(item => 
          item.location?.toLowerCase() === value.toLowerCase()
        );
        break;
      case 'vendor':
        itemsToAdd = inventoryData.filter(item => 
          item.brand?.toLowerCase() === value.toLowerCase()
        );
        break;
      case 'all':
        itemsToAdd = [...inventoryData];
        break;
    }

    // Add items that aren't already selected
    const newItems = itemsToAdd.filter(item => 
      !selectedItems.find(selected => selected.id === item.id)
    );
    
    setSelectedItems(prev => [...prev, ...newItems]);
  };

  const removeItem = (itemId) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
  };

  const toggleItem = (item) => {
    const isSelected = selectedItems.find(selected => selected.id === item.id);
    if (isSelected) {
      removeItem(item.id);
    } else {
      setSelectedItems(prev => [...prev, item]);
    }
  };

  // Database browser references and state
  const browserApiRef = useRef(null);
  const modalBrowserApiRef = useRef(null);

  const handleSelectAllFiltered = () => {
    if (step === 'customize-template' && browserApiRef.current) {
      browserApiRef.current.checkAll();
    } else if (modalBrowserApiRef.current) {
      modalBrowserApiRef.current.checkAll();
    }
  };

  const handleDeselectAllFiltered = () => {
    if (step === 'customize-template' && browserApiRef.current) {
      browserApiRef.current.uncheckAll();
    } else if (modalBrowserApiRef.current) {
      modalBrowserApiRef.current.uncheckAll();
    }
  };

  const handleAddSelectedItems = () => {
    const newItems = inventoryData.filter(item => databaseCheckedItems.includes(item.id));
    setSelectedItems(prev => [...prev, ...newItems]);
    // Clear checkmarks after adding
    if (step === 'customize-template' && browserApiRef.current) {
      browserApiRef.current.uncheckAll();
    } else if (modalBrowserApiRef.current) {
      modalBrowserApiRef.current.uncheckAll();
    }
  };

  // Handler for adding items from database browser
  const handleAddFromDatabase = async (item) => {
    console.log('🔍 [CountSessionManager] Adding item from database browser:', item.description);

    // Check if item is already selected
    const isAlreadySelected = selectedItems.find(selected => selected.id === item.id);
    if (isAlreadySelected) {
      console.log('⚠️ [CountSessionManager] Item already selected:', item.description);
      return;
    }

    // Optimistically add to local state immediately
    setSelectedItems(prev => [...prev, item]);
    console.log('✅ [CountSessionManager] Added item to selection:', item.description);

    // When editing an existing session, immediately persist to DB so
    // the item shows up in the count list without requiring a full Save.
    if (isEditMode && editingSessionId) {
      try {
        const { data: inserted, error } = await supabase
          .from('count_items')
          .insert({
            count_session_id: editingSessionId,
            inventory_item_id: item.id,
            item_snapshot: item,
            location: item.location || null,
            expected_quantity: '',
            actual_quantity: '',
            variance_quantity: 0,
            count_notes: '',
            is_counted: false,
            counted_at: null
          })
          .select()
          .single();

        if (error) {
          console.error('❌ [CountSessionManager] Failed to persist added item to DB:', error);
          // Revert optimistic state update on failure
          setSelectedItems(prev => prev.filter(i => i.id !== item.id));
        } else {
          console.log('✅ [CountSessionManager] Item persisted to count_items:', inserted?.id);
          // Update local item to carry the count_item_id for future operations
          setSelectedItems(prev => prev.map(i =>
            i.id === item.id ? { ...i, count_item_id: inserted?.id } : i
          ));
        }
      } catch (err) {
        console.error('❌ [CountSessionManager] Unexpected error persisting item:', err);
        setSelectedItems(prev => prev.filter(i => i.id !== item.id));
      }
    }
  };

  // Handler for clicking items in database browser (for expansion/details)
  const handleDatabaseItemClick = (item) => {
    console.log('🔍 [CountSessionManager] Database item clicked:', item.description);
  };

  const startCounting = () => {
    // Initialize count data
    const countData = selectedItems.map(item => ({
      ...item,
      expected_qty: item.case_qty || '',
      actual_qty: '',
      variance: 0,
      notes: ''
    }));
    setSelectedItems(countData);
    setStep('count');
  };

  const updateCount = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        
        // Calculate variance if both expected and actual are numbers
        if (field === 'actual_qty' || field === 'expected_qty') {
          const expected = parseFloat(updated.expected_qty) || 0;
          const actual = parseFloat(updated.actual_qty) || 0;
          updated.variance = actual - expected;
        }
        
        return updated;
      }
      return item;
    }));
  };

  const saveTemplate = async () => {
    if (!templateData.name.trim()) {
      alert('Template name is required');
      return false;
    }

    if (selectedItems.length === 0) {
      alert('Please select at least one item for the template');
      return false;
    }

    setLoading(true);
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        alert('You must be logged in to save templates');
        return false;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Saving template for user:', user.email, 'store:', storeId);

      // Pick a least-used palette color so the new template is visually
      // distinguishable from this store's existing ones. The DB trigger will
      // also assign one if we ever ship a null, but doing it here lets the
      // UI render the color immediately on the next load.
      let nextColor = null;
      try {
        const { data: existing } = await supabase
          .from('count_templates')
          .select('template_color')
          .eq('store_id', storeId);
        nextColor = pickNextTemplateColor(existing || []);
      } catch (_) {
        nextColor = pickNextTemplateColor([]);
      }

      const newTemplate = {
        store_id: storeId,
        created_by: user.id,
        name: templateData.name,
        description: templateData.description || `Template with ${selectedItems.length} items`,
        category: templateData.category,
        items: selectedItems.map(item => item.id),
        template_color: nextColor
      };

      const { data: inserted, error } = await supabase
        .from('count_templates')
        .insert([newTemplate])
        .select('id')
        .single();

      if (error) {
        console.error('Error saving template:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        alert('Error saving template: ' + (error.message || error.details || JSON.stringify(error)));
        return false;
      }

      alert('Template saved successfully!');
      loadTemplates(); // Reload templates
      // Pass the just-saved template's ID so the modal only fires when the new
      // template itself is part of a suspicious group (not on unrelated saves).
      runMisuseDetection(undefined, undefined, inserted?.id);

      // Reset form
      setTemplateData({ name: '', description: '', category: 'custom' });
      setSelectedItems([]);
      setStep('dashboard');

      return true;
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error details:', error.message || error);
      alert('Error saving template: ' + (error.message || JSON.stringify(error)));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadFromTemplate = (template) => {
    setSelectedTemplate(template);
    // Load items from template
    const templateItems = inventoryData.filter(item =>
      template.items.includes(item.id)
    );
    setSelectedItems(templateItems);

    setCountSession(prev => ({
      ...prev,
      template_id: template.id,
      session_date: getCurrentLocalDate(),
      created_time: getCurrentLocalTime(),
      count_commence_time: getCurrentLocalTime()
    }));
    setStep('customize');
    // Name will be auto-generated by useEffect
  };

  const saveCountSession = async () => {
    setLoading(true);
    try {
      // Skip hanging Supabase auth calls, use localStorage directly
      const storedUser = localStorage.getItem('currentUser');
      const storedStoreId = localStorage.getItem('selectedStoreId');

      if (!storedUser || !storedStoreId) {
        alert('You must be logged in to save count sessions');
        return;
      }

      const user = JSON.parse(storedUser);
      const storeId = parseInt(storedStoreId);
      console.log('✅ [CountSessionManager] Saving count session for user:', user.email, 'store:', storeId);

      if (isEditMode && editingSessionId) {
        // UPDATE EXISTING SESSION
        console.log('📝 Updating existing session:', editingSessionId);
        
        // Update session data
        const sessionUpdate = {
          name: countSession.name,
          session_date: countSession.session_date,
          created_time: countSession.created_time,
          count_commence_time: countSession.count_commence_time,
          notes: countSession.notes,
          total_items: selectedItems.length,
          updated_at: new Date().toISOString()
        };

        const { error: sessionError } = await supabase
          .from('count_sessions')
          .update(sessionUpdate)
          .eq('id', editingSessionId);

        if (sessionError) {
          console.error('Error updating session:', sessionError);
          alert('Error updating count session: ' + sessionError.message);
          return;
        }

        // Handle count items updates
        await updateCountItems(editingSessionId, selectedItems);

      } else {
        // CREATE NEW SESSION (existing logic)
      const sessionData = {
        store_id: storeData.store_id,
        created_by: user.id,
        name: countSession.name,
        template_id: countSession.template_id,
        template_name_snapshot: selectedTemplate?.name || null,
        template_type_snapshot: selectedTemplate?.category || null,
        session_date: countSession.session_date,
        created_time: countSession.created_time,
        count_commence_time: countSession.count_commence_time,
        user_timezone: userTimezone,
        notes: countSession.notes,
        status: 'draft',
        total_items: selectedItems.length,
        items_counted: selectedItems.filter(item => item.actual_qty).length,
        completed_at: new Date().toISOString()
      };

      const { data: sessionResult, error: sessionError } = await supabase
        .from('count_sessions')
        .insert([sessionData])
        .select()
        .single();

      if (sessionError) {
          console.error('Error creating session:', sessionError);
        console.error('Error details:', JSON.stringify(sessionError, null, 2));
        alert('Error saving count session: ' + (sessionError.message || sessionError.details || JSON.stringify(sessionError)));
        return;
      }

        // Create count items for new session. If this fails the session row
        // is already in the database and would strand as an empty draft that
        // dead-ends at "No items in this session" — remove it before failing.
        try {
          await createCountItems(sessionResult.id, selectedItems);
        } catch (itemsErr) {
          await supabase.from('count_sessions').delete().eq('id', sessionResult.id);
          throw itemsErr;
        }
      }

      console.log('✅ Count session saved successfully');
      showToastMessage(isEditMode ? 'Count Updated Successfully' : 'Count Created Successfully');
      
      // Reset and return to dashboard
      resetForm();
      setStep('dashboard');
      loadCountSessions();
      
    } catch (error) {
      console.error('Error saving count session:', error);
      console.error('Error details:', error.message || error);
      alert('Error saving count session: ' + (error.message || JSON.stringify(error)));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to update count items for existing sessions
  const updateCountItems = async (sessionId, items) => {
    // Get existing count items
    const { data: existingItems, error: fetchError } = await supabase
      .from('count_items')
      .select('*')
      .eq('count_session_id', sessionId);

    if (fetchError) {
      throw new Error('Error fetching existing items: ' + fetchError.message);
    }

    // Separate items into different operations
    const itemsToUpdate = [];
    const itemsToInsert = [];
    const itemsToDelete = [];

    // Check which existing items to keep, update, or delete
    existingItems.forEach(existingItem => {
      const correspondingItem = items.find(item => 
        item.count_item_id === existingItem.id || 
        item.id === existingItem.inventory_item_id
      );

      if (correspondingItem) {
        // Don't allow deletion of counted items
        if (existingItem.is_counted) {
          console.log(`⚠️ Cannot modify counted item: ${existingItem.inventory_item_id}`);
          return;
        }
        
        // Update existing item
        itemsToUpdate.push({
          id: existingItem.id,
          expected_quantity: correspondingItem.expected_qty || '',
          actual_quantity: correspondingItem.actual_qty || '',
          variance_quantity: parseFloat(correspondingItem.variance) || 0,
          count_notes: correspondingItem.notes || ''
        });
      } else {
        // Item was removed - only allow if not counted
        if (!existingItem.is_counted) {
          itemsToDelete.push(existingItem.id);
        } else {
          console.log(`⚠️ Cannot delete counted item: ${existingItem.inventory_item_id}`);
        }
      }
    });

    // Find new items to insert
    items.forEach(item => {
      const existsAlready = existingItems.find(existing => 
        existing.inventory_item_id === item.id
      );
      
      if (!existsAlready) {
        const copiedParDefaulted = item.par_defaulted ?? item.item_snapshot?.par_defaulted;
        const copiedParUnit = copiedParDefaulted === true
          ? (item.item_snapshot?.unit || item.unit || item.par_unit || 'each')
          : (item.par_unit || item.item_snapshot?.unit || item.unit || 'each');
        itemsToInsert.push({
          count_session_id: sessionId,
          inventory_item_id: item.id,
          item_snapshot: item,
          expected_quantity: item.expected_qty || '',
          actual_quantity: item.actual_qty || '',
          variance_quantity: parseFloat(item.variance) || 0,
          count_notes: item.notes || '',
          is_counted: !!item.actual_qty,
          counted_at: item.actual_qty ? new Date().toISOString() : null,
          par_level: item.par_level === '' || item.par_level == null ? null : item.par_level,
          par_unit: copiedParUnit,
          ...(typeof copiedParDefaulted === 'boolean'
            ? { par_defaulted: copiedParDefaulted }
            : {}),
        });
      }
    });

    // Execute operations
    if (itemsToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('count_items')
        .delete()
        .in('id', itemsToDelete);
        
      if (deleteError) {
        throw new Error('Error deleting items: ' + deleteError.message);
      }
      console.log(`🗑️ Deleted ${itemsToDelete.length} uncounted items`);
    }

    if (itemsToUpdate.length > 0) {
      for (const updateItem of itemsToUpdate) {
        const { error: updateError } = await supabase
          .from('count_items')
          .update(updateItem)
          .eq('id', updateItem.id);
          
        if (updateError) {
          throw new Error('Error updating item: ' + updateError.message);
        }
      }
      console.log(`📝 Updated ${itemsToUpdate.length} items`);
    }

    if (itemsToInsert.length > 0) {
      const { error: insertError } = await supabase
        .from('count_items')
        .insert(itemsToInsert);
        
      if (insertError) {
        throw new Error('Error adding new items: ' + insertError.message);
      }
      console.log(`➕ Added ${itemsToInsert.length} new items`);
    }
  };

  // Helper function to create count items for new sessions
  const createCountItems = async (sessionId, items) => {
    const countItems = items.map((item, index) => {
      const copiedParDefaulted = item.par_defaulted ?? item.item_snapshot?.par_defaulted;
      const copiedParUnit = copiedParDefaulted === true
        ? (item.item_snapshot?.unit || item.unit || item.par_unit || 'each')
        : (item.par_unit || item.item_snapshot?.unit || item.unit || 'each');
      return {
        count_session_id: sessionId,
        inventory_item_id: item.id,
        item_snapshot: item,
        expected_quantity: item.expected_qty || '',
        actual_quantity: item.actual_qty || '',
        variance_quantity: parseFloat(item.variance) || 0,
        count_notes: item.notes || '',
        is_counted: !!item.actual_qty,
        counted_at: item.actual_qty ? new Date().toISOString() : null,
        // Copy PAR from template to count_items table. Vendor minimums live on
        // purchase_options — never seed min_order_qty per item. Nullish checks
        // preserve an explicit PAR zero, and known provenance is copied even
        // when the confirmed value is still numerically one.
        par_level: item.par_level === '' || item.par_level == null ? null : item.par_level,
        par_unit: copiedParUnit,
        ...(typeof copiedParDefaulted === 'boolean'
          ? { par_defaulted: copiedParDefaulted }
          : {}),
        min_order_qty: null,
        min_order_unit: item.min_order_unit || 'each',
        location: item.location || item.item_snapshot?.location || null,
        count_position: item.count_position || (index + 1) * 10,
      };
    });

    const { error: itemsError } = await supabase
      .from('count_items')
      .insert(countItems);

    if (itemsError) {
      throw new Error('Error creating count items: ' + itemsError.message);
    }
  };

  // Helper function to reset form state
  const resetForm = () => {
      setSelectedItems([]);
      setSelectedTemplatesForSession([]);
      setCountSession({
        name: '',
        session_date: new Date().toISOString().split('T')[0],
        created_time: getCurrentLocalTime(),
        count_commence_time: getCurrentLocalTime(),
        notes: ''
      });
    setIsEditMode(false);
    setEditingSessionId(null);
  };

  // Delete session — moves to trash instead of hard delete.
  // Completed sessions may NOT be trashed through this path: they must be
  // archived (stop using) or voided (mark invalid, preserve audit trail).
  const handleDeleteSession = async (sessionId) => {
    const allSessions = [...countSessions, ...completedSessions];
    const session = allSessions.find(s => s.id === sessionId);

    if (session && session.status === 'completed') {
      const archiveChoice = confirm(
        'Completed counts cannot be moved to trash — they contain business history.\n\n' +
        'Archive moves the count off the active dashboard to the Archive page. The historical data stays intact and visible in reports.\n\n' +
        'Void marks the count as invalid, removing it from all report totals. An audit record remains in the Archive page.\n\n' +
        'Click OK to Archive, or Cancel to Void.'
      );
      if (archiveChoice) {
        try {
          setLoading(true);
          const { error } = await supabase
            .from('count_sessions')
            .update({ is_archived: true, archived_at: new Date().toISOString() })
            .eq('id', sessionId);
          if (error) throw error;
          showToastMessage('Session archived successfully');
          loadCountSessions();
        } catch (err) {
          console.error('Error archiving session:', err);
          alert('Error archiving session: ' + err.message);
        } finally {
          setLoading(false);
        }
      } else {
        await handleVoidSession(session);
      }
      return;
    }

    // Draft / in-progress sessions: three-way prompt — archive, trash, or cancel
    const archiveInstead = confirm(
      'This will remove the counted information from reports.\n\nWas this count a mistake or do you wish to archive?\n\nClick OK to Archive, or Cancel to continue to Trash.'
    );

    if (archiveInstead) {
      try {
        setLoading(true);
        const { error } = await supabase
          .from('count_sessions')
          .update({ is_archived: true, archived_at: new Date().toISOString() })
          .eq('id', sessionId);
        if (error) throw error;
        showToastMessage('Session archived successfully');
        loadCountSessions();
      } catch (error) {
        console.error('Error archiving session:', error);
        alert('Error archiving session: ' + error.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!confirm('Move this count session to trash? You can recover it later from the Trash page.')) {
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('count_sessions')
        .update({ is_trashed: true, trashed_at: new Date().toISOString() })
        .eq('id', sessionId);
      if (error) throw error;
      showToastMessage('Session moved to trash');
      loadCountSessions();
    } catch (error) {
      console.error('Error trashing session:', error);
      alert('Error moving to trash: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderCreateTemplateStep = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">Create Count Template</h5>
        <p className="text-muted mb-0">Define a reusable list of items for counting (no time scheduling)</p>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label">Template Name *</label>
            <input
              type="text"
              className="form-control"
              value={templateData.name}
              onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Weekly Liquor Count, Monthly Full Count"
              autoComplete="off"
              data-lpignore="true"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Category</label>
            <select
              className="form-control"
              value={templateData.category}
              onChange={(e) => setTemplateData(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-12">
            <label className="form-label">Description (Optional)</label>
            <textarea
              className="form-control"
              rows="2"
              value={templateData.description}
              onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this template is used for..."
            />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 p-3" style={{ backgroundColor: '#F5F3F0', borderRadius: '8px', border: '1px solid #D4C4B0' }}>
          <strong style={{ color: '#5A4A42' }}>Selected Items: {selectedItems.length}</strong>
        </div>

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary" onClick={() => setStep('dashboard')}>
            <i className="feather icon-arrow-left me-1"></i>
            Back to Dashboard
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setStep('customize-template')}
            disabled={selectedItems.length === 0 || !templateData.name.trim()}
          >
            <i className="feather icon-edit me-1"></i>
            Customize Template ({selectedItems.length})
          </button>
        </div>
      </div>
    </div>
  );

  const renderCustomizeTemplateStep = () => (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Create Count Template</h5>
            <p className="text-muted mb-0">Add items and organize them by location with drag & drop</p>
          </div>
          <span className="badge bg-primary fs-6">{selectedItems.length} items selected</span>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="row g-0" style={{ height: '70vh' }}>

          {/* LEFT COLUMN: Count Preview with Drag & Drop */}
          <div className="col-md-6" style={{ borderRight: '1px solid #dee2e6' }}>
            <div className="p-3 h-100 d-flex flex-column">
              <div className="mb-3">
                <h6>
                  <i className="feather icon-eye me-2"></i>
                  Count Preview - Drag to Reorder
                </h6>
                <div className="alert alert-success py-2">
                  <div className="row">
                    <div className="col-md-8">
                      <small>
                        <strong>This is exactly how your count will look!</strong> Items are organized by location.
                        Drag items up or down to match your physical shelf layout.
                      </small>
                    </div>
                    <div className="col-md-4 text-end">
                      <small className="text-muted">
                        <i className="feather icon-move me-1"></i>
                        Drag & Drop Enabled
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                {selectedItems.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="feather icon-package" style={{ fontSize: '2rem' }}></i>
                    <p className="mt-2">No items added yet. Add items from the database browser on the right.</p>
                  </div>
                ) : (
                  <div>
                    {/* Group items by location */}
                    {Object.entries(
                      selectedItems.reduce((groups, item) => {
                        const location = item.location || 'No Location';
                        if (!groups[location]) groups[location] = [];
                        groups[location].push(item);
                        return groups;
                      }, {})
                    ).map(([location, items]) => (
                      <div key={location} className="mb-4">
                        <div className="d-flex align-items-center mb-2">
                          <h6 className="mb-0 text-primary">
                            <i className="feather icon-map-pin me-2"></i>
                            {location}
                          </h6>
                          <span className="badge bg-light text-dark ms-2">{items.length} items</span>
                        </div>

                        {items.map((item, index) => (
                          <div
                            key={item.id}
                            className="card mb-2"
                            style={{
                              cursor: 'move',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <div className="card-body p-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <i className="feather icon-move me-2 text-muted"></i>
                                  <div>
                                    <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                      {item.description}
                                    </div>
                                    {renderPurchaseOptionSummary(item.id)}
                                    <small className="text-muted">
                                      {item.brand && `${item.brand} | `}
                                      {item.category}
                                      {item.unit && ` | ${item.unit}`}
                                    </small>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeItem(item.id)}
                                  title="Remove item"
                                >
                                  <i className="feather icon-x"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Database Browser */}
          <div className="col-md-6" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="p-2 border-bottom bg-white d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSelectAllFiltered}
                >
                  Select All{databaseFilteredItems.length > 0 && databaseFilteredItems.length < inventoryData.length ? ` (${databaseFilteredItems.length})` : ''}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDeselectAllFiltered}
                  disabled={databaseCheckedItems.length === 0}
                >
                  Deselect All
                </button>
              </div>
              <button
                className="btn btn-sm"
                style={{ backgroundColor: '#2e7d32', borderColor: '#2e7d32', color: '#fff' }}
                onClick={handleAddSelectedItems}
                disabled={databaseCheckedItems.length === 0}
              >
                Add Selected ({databaseCheckedItems.length})
              </button>
            </div>
            <div className="flex-grow-1" style={{ minHeight: 0 }}>
              <DatabaseColumn
                inventoryData={inventoryData}
                currentItems={selectedItems}
                onAddItem={handleAddFromDatabase}
                onItemClick={handleDatabaseItemClick}
                onFilteredListChange={setDatabaseFilteredItems}
                config={{
                  title: "Add Items from Database",
                  subtitle: "Search and add items to your template",
                  icon: "database",
                  showHeader: true
                }}
                browserConfig={{
                  ...getDatabaseBrowserPreset('COUNT_SESSION'),
                  selectable: true,
                  browserApiRef: browserApiRef,
                  onCheckedItemsChange: setDatabaseCheckedItems
                }}
                styling={{
                  column: {
                    height: '100%',
                    border: 'none',
                    borderRadius: 0,
                    backgroundColor: 'white'
                  },
                  header: {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="card-footer bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <button className="btn btn-secondary" onClick={() => setStep('create-template')}>
              <i className="feather icon-arrow-left me-1"></i>
              Back
            </button>

            <div className="text-center">
              <strong>Template: {templateData.name}</strong>
              <br />
              <small className="text-muted">{selectedItems.length} items selected</small>
            </div>

            <button
              className="btn btn-primary"
              onClick={saveTemplate}
              disabled={selectedItems.length === 0 || !templateData.name.trim() || loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="feather icon-save me-1"></i>
                  Save Template ({selectedItems.length} items)
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading inventory data...</p>
      </div>
    );
  }

  // New Tooltip Functions
  const showTooltip = (event, template, templateItems) => {
    // Remove any existing tooltip for this template
    hideTooltip(template.id);

    // Get button position
    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.id = `tooltip-${template.id}`;
    tooltip.className = 'template-tooltip-new';

    // Style the tooltip
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.25);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      font-size: 0.75rem;
      line-height: 1.4;
      z-index: 999999;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease, visibility 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      pointer-events: none;
      max-width: 300px;
      min-width: 250px;
    `;

    // Get actual items for this template
    const actualItems = Array.isArray(template.items)
      ? inventoryData.filter(item => template.items.includes(item.id))
      : [];

    // Create tooltip content with item list
    tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; font-size: 0.8rem;">${template.name}</div>
      <div style="margin-bottom: 8px; font-size: 0.7rem; opacity: 0.9;">
        <i class="feather icon-package" style="margin-right: 4px;"></i>
        ${actualItems.length} items
      </div>
      <div style="font-size: 0.65rem; max-height: 200px; overflow-y: auto;">
        ${actualItems.slice(0, 8).map(item => `
          <div style="margin-bottom: 3px; padding: 2px 0;">
            • ${item.description || item.name || 'Unknown Item'}
            ${item.location ? `<span style="opacity: 0.7; font-size: 0.6rem;"> (${item.location})</span>` : ''}
          </div>
        `).join('')}
        ${actualItems.length > 8 ? `
          <div style="font-style: italic; opacity: 0.7; margin-top: 6px; text-align: center;">
            ...and ${actualItems.length - 8} more items
          </div>
        ` : ''}
      </div>
    `;

    // Position tooltip to the left of the button
    const tooltipWidth = 250;
    let left = buttonRect.left - tooltipWidth - 15;
    let top = buttonRect.top - 10;

    // Keep tooltip within viewport
    if (left < 10) {
      left = buttonRect.right + 15;
    }
    if (left + tooltipWidth > window.innerWidth - 10) {
      left = window.innerWidth - tooltipWidth - 10;
    }
    if (top < 10) {
      top = 10;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;

    // Add to document
    document.body.appendChild(tooltip);

    // Show after delay
    setTimeout(() => {
      if (document.getElementById(`tooltip-${template.id}`)) {
        tooltip.style.opacity = '1';
        tooltip.style.visibility = 'visible';
      }
    }, 1200); // 1.2 second delay

    console.log(`🎯 [NEW-TOOLTIP] Created tooltip for template ${template.id} with ${actualItems.length} items`);
  };

  const hideTooltip = (templateId) => {
    const tooltip = document.getElementById(`tooltip-${templateId}`);
    if (tooltip) {
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'hidden';
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      }, 300);
    }
  };

  const renderDashboard = () => {
    // For count-creation mode, render simplified structure without extra wrappers
    if (mode === 'count-creation') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%'
        }}>
          {!loading && templates.length === 0 ? null : (
            <div
              className="template-grid-container"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '1rem',
                padding: '1rem',
                width: '100%',
                height: '100%',
                minHeight: '0',
                maxHeight: 'none',
                overflowX: 'visible',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#8A7A6B #F5F3F0',
                position: 'relative',
                zIndex: 1,
                contain: 'layout style paint',
                boxSizing: 'border-box',
                flex: '1 1 auto'
              }}
            >
              {/* Sort templates by selected criteria */}
              {getSortedTemplates(templates)
                .slice(0, templatesDisplayCount)
                .map(template => {
                // Get template items for analysis
                const templateItems = Array.isArray(template.items)
                  ? inventoryData.filter(item => template.items.includes(item.id))
                  : [];

                // Get top 3 categories
                const categoryCount = {};
                templateItems.forEach(item => {
                  if (item.category) {
                    categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
                  }
                });
                const topCategories = Object.entries(categoryCount)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 3)
                  .map(([category]) => category);

                // Format created date
                const createdDate = template.created_at
                  ? new Date(template.created_at).toLocaleDateString()
                  : 'Unknown';

                const accentColor = getTemplateColor(template);
                const isSelected = selectedTemplates.includes(template.id);

                return (
                  <div key={template.id}>
                    <div className="template-tooltip-wrapper position-relative"
                      style={{ position: 'relative', overflow: 'visible' }}
                      onMouseEnter={(e) => showTooltip(e, template, templateItems)}
                      onMouseLeave={() => hideTooltip(template.id)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/count-template', JSON.stringify({ id: template.id, name: template.name }));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                    >
                      <div className={`card position-relative ${template.order_calculations_enabled ? 'order-enabled-card' : ''}`} style={{
                        backgroundColor: isSelected ? '#8A7A6B' : '#F5F3F0',
                        border: isSelected ? '2px solid #5A4A42' : '1px solid #8A7A6B',
                        borderLeft: `4px solid ${accentColor}`,
                        minHeight: '108px',
                        width: '100%',
                        maxWidth: '260px',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 2px 4px rgba(90, 74, 66, 0.1)',
                        transition: 'all 0.2s ease',
                        margin: '0 auto',
                        fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden'
                      }}>
                      <div className="card-body py-2 px-2 d-flex flex-column" style={{ zIndex: 1 }}>
                        <div className="position-absolute d-flex align-items-start gap-1" style={{ top: '6px', right: '6px', left: '6px', justifyContent: 'space-between' }}>
                          {template.archived && (
                            <span className="badge" style={{
                              backgroundColor: '#6c757d',
                              color: 'white',
                              fontSize: '0.55rem',
                              padding: '2px 5px'
                            }}>
                              Archived
                            </span>
                          )}
                          <input
                            type="checkbox"
                            className="form-check-input ms-auto"
                            checked={selectedTemplates.includes(template.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTemplates([...selectedTemplates, template.id]);
                              } else {
                                setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                              }
                            }}
                            style={{ transform: 'scale(1.05)', flexShrink: 0 }}
                          />
                        </div>

                        <h6 className="fw-bold mb-1 pe-4" style={{
                          fontSize: '0.85rem',
                          color: isSelected ? 'white' : accentColor,
                          lineHeight: '1.25',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          marginTop: template.archived ? '1.1rem' : 0
                        }}>
                          {template.name}
                        </h6>

                        <div className="small mb-1" style={{
                          fontSize: '0.72rem',
                          color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.92)' : '#5A4A42',
                          lineHeight: 1.3
                        }}>
                          <span className="fw-semibold">{templateItems.length}</span>
                          {' items'}
                          {topCategories.length > 0 && (
                            <>
                              <span className="text-muted mx-1" style={{ opacity: selectedTemplates.includes(template.id) ? 0.7 : 1 }}>·</span>
                              <span style={{ color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.85)' : '#8A7A6B' }}>
                                {topCategories.slice(0, 2).join(', ')}
                                {Object.keys(categoryCount).length > 2 ? '…' : ''}
                              </span>
                            </>
                          )}
                        </div>

                        <div className="small mt-auto mb-2" style={{
                          fontSize: '0.68rem',
                          color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.65)' : '#9A8B82'
                        }}>
                          {(() => {
                            const creator = template.creatorName
                              || (currentUser && template.created_by === currentUser.id
                                  ? currentUser.compactName || currentUser.displayName
                                  : null);
                            return creator
                              ? `${createdDate} · ${creator}`
                              : `Created ${createdDate}`;
                          })()}
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm position-relative"
                            style={{
                              fontSize: '0.65rem',
                              padding: '0.2rem 0.55rem',
                              minHeight: '26px',
                              backgroundColor: '#28a745',
                              border: '1px solid #28a745',
                              color: 'white'
                            }}
                            title="Include in count"
                            onClick={() => {
                              if (window.addTemplateToCount) {
                                window.addTemplateToCount(template);
                              }
                            }}
                          >
                            Include
                          </button>
                        </div>
                      </div>
                    </div>


                  </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // For normal mode, render full dashboard with tabs and cards
    return (
    <>
      {/* Tab Navigation */}
      <div className="mb-4">
        <nav className="nav nav-tabs" style={{ borderBottom: '2px solid #D4C4B0' }}>
          <button
            className={`nav-link ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
            style={{
              color: activeTab === 'templates' ? '#5A4A42' : '#8A7A6B',
              backgroundColor: activeTab === 'templates' ? '#F5F3F0' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'templates' ? '2px solid #8A7A6B' : '2px solid transparent',
              fontWeight: '600'
            }}
          >
            <i className="feather icon-file-text me-2"></i>
            Manage groups
          </button>

        </nav>
      </div>



      <div className="card">
        <div className="card-body">

        {showCalendarView ? (
          <>
            {/* Template Section - Full Width (Calendar removed) */}
            <div className="row">
              <div className="col-12">
                <div className="card" style={{
                    backgroundColor: 'rgb(245, 243, 240)',
                    border: '1px solid rgb(212, 196, 176)'
                }}>
                  <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'transparent' }}>
                      <h6 className="mb-0" style={{ color: 'rgb(90, 74, 66)' }}>Manage Item Groups</h6>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-info btn-sm"
                          onClick={() => setShowArchiveView(true)}
                        >
                          <i className="feather icon-archive me-1"></i>
                          View Archive
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2e7d32', borderColor: '#2e7d32', color: '#fff' }}
                          onClick={() => {
                            setEditingGroupForEditor(null);
                            setStep('location-template-creator');
                          }}
                        >
                          <i className="feather icon-plus me-1"></i>
                          New Item Group
                        </button>
                      </div>
                  </div>
                  <div className="card-body p-3">

                    {!loading && templates.length === 0 ? (
                      <div className="text-center p-4" style={{ color: '#8A7A6B' }}>
                        <i className="feather icon-file-plus" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}></i>
                        <p className="mb-3">No groups yet. Create a group to define reusable item lists for counting.</p>
                <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setEditingGroupForEditor(null);
                            setStep('location-template-creator');
                          }}
                          style={{
                            backgroundColor: '#8A7A6B',
                            borderColor: '#8A7A6B'
                          }}
                        >
                          <i className="feather icon-plus me-1"></i>
                          Create first group
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Template Management Controls - Simplified */}
                        <div className="mb-3 p-3" style={{ 
                          backgroundColor: '#F8F9FA', 
                          borderRadius: '8px', 
                          border: '1px solid #DEE2E6' 
                        }}>
                          <div className="d-flex justify-content-between align-items-center" style={{
                            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
                            fontSize: '16px',
                            fontWeight: '400',
                            color: 'rgb(33, 37, 41)',
                            padding: '16px',
                            marginBottom: '16px',
                            marginTop: '0',
                            marginLeft: '0',
                            marginRight: '0'
                          }}>
                            <div className="d-flex align-items-center gap-3" style={{
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontFamily: "'Open Sans', sans-serif",
                              fontSize: '16px',
                              fontWeight: '400',
                              color: '#5A4A42',
                              padding: '0',
                              margin: '0'
                            }}>

                              {/* Custom Filter Dropdown */}
                              <div className="custom-dropdown template-filter-dropdown" style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={() => setTemplateFilterDropdownOpen(!templateFilterDropdownOpen)}
                                  style={{
                                    fontSize: '0.9rem',
                                    border: '1px solid #D4C4B0',
                                    borderRadius: '4px',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    color: '#5A4A42',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'border-color 0.2s ease',
                                    fontFamily: "'Open Sans', sans-serif",
                                    minWidth: '160px'
                                  }}
                                  onMouseEnter={(e) => e.target.style.borderColor = '#C8B5A0'}
                                  onMouseLeave={(e) => e.target.style.borderColor = '#D4C4B0'}
                                >
                                  <i className="feather icon-filter"></i>
                                  Filter: {getFilterDescription()}
                                  <i className="feather icon-chevron-down" style={{ marginLeft: 'auto' }}></i>
                                </button>

                                {templateFilterDropdownOpen && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    right: '0',
                                    background: '#fff',
                                    border: '1px solid #D4C4B0',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(90, 74, 66, 0.1)',
                                    zIndex: 1000,
                                    marginTop: '2px',
                                    fontFamily: "'Open Sans', sans-serif",
                                    minWidth: '200px'
                                  }}>
                                    {/* Status Filter Section */}
                                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #F0F0F0', fontSize: '0.8rem', fontWeight: 'bold', color: '#8B7B73' }}>
                                      Archive Status
                                    </div>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, status: 'all' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.status === 'all' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'all' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-list"></i>
                                      All groups
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, status: 'active' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.status === 'active' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'active' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-check-circle"></i>
                                      Active Only
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, status: 'archived' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.status === 'archived' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'archived' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-archive"></i>
                                      Archived Only
                                    </button>

                                    {/* Order Type Filter Section */}
                                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #F0F0F0', borderTop: '1px solid #F0F0F0', fontSize: '0.8rem', fontWeight: 'bold', color: '#8B7B73' }}>
                                      Order Type
                                    </div>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, orderType: 'all' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.orderType === 'all' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'all' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-layers"></i>
                                      All Types
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, orderType: 'order' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.orderType === 'order' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'order' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-shopping-cart"></i>
                                      Order & Count
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateFilters(prev => ({ ...prev, orderType: 'count-only' }));
                                        setTemplateFilterDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateFilters.orderType === 'count-only' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'count-only' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-check-square"></i>
                                      Count Only
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Custom Sort Dropdown */}
                              <div className="custom-dropdown template-sort-dropdown" style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={() => setTemplateSortDropdownOpen(!templateSortDropdownOpen)}
                                  style={{
                                    fontSize: '0.9rem',
                                    border: '1px solid #D4C4B0',
                                    borderRadius: '4px',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    color: '#5A4A42',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'border-color 0.2s ease',
                                    fontFamily: "'Open Sans', sans-serif",
                                    minWidth: '160px'
                                  }}
                                  onMouseEnter={(e) => e.target.style.borderColor = '#C8B5A0'}
                                  onMouseLeave={(e) => e.target.style.borderColor = '#D4C4B0'}
                                >
                                  <i className="feather icon-sort"></i>
                                  Sort: {templateSortBy === 'name' ? 'Name' : templateSortBy === 'created_at' ? 'Created' : 'Last Used'}
                                  <i className="feather icon-chevron-down" style={{ marginLeft: 'auto' }}></i>
                                </button>
                                
                                {templateSortDropdownOpen && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    right: '0',
                                    background: '#fff',
                                    border: '1px solid #D4C4B0',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(90, 74, 66, 0.1)',
                                    zIndex: 1000,
                                    marginTop: '2px',
                                    fontFamily: "'Open Sans', sans-serif",
                                    minWidth: '160px'
                                  }}>
                                    <button
                                      onClick={() => {
                                        setTemplateSortBy('name');
                                        setTemplateSortDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateSortBy === 'name' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateSortBy === 'name' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-type"></i>
                                      Sort by Name
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateSortBy('created_at');
                                        setTemplateSortDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateSortBy === 'created_at' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateSortBy === 'created_at' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-calendar"></i>
                                      Sort by Date Created
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTemplateSortBy('last_used');
                                        setTemplateSortDropdownOpen(false);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: 'none',
                                        background: templateSortBy === 'last_used' ? '#FAF8F6' : 'transparent',
                                        color: '#5A4A42',
                                        fontSize: '0.9rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontFamily: "'Open Sans', sans-serif"
                                      }}
                                      onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                                      onMouseLeave={(e) => e.target.style.background = templateSortBy === 'last_used' ? '#FAF8F6' : 'transparent'}
                                    >
                                      <i className="feather icon-clock"></i>
                                      Sort by Last Used
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {selectedTemplates.length > 0 && (
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => {
                                    setTemplatesToArchive(selectedTemplates);
                                    setShowArchiveModal(true);
                                  }}
                                >
                                  <i className="feather icon-archive me-1"></i>
                                  Archive ({selectedTemplates.length})
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    if (selectedTemplates.length === 1) {
                                      setTemplateToDelete(selectedTemplates[0]);
                                      setShowDeleteModal(true);
                                    } else {
                                      alert('Please select only one template to delete at a time.');
                                    }
                                  }}
                                  disabled={selectedTemplates.length !== 1}
                                >
                                  <i className="feather icon-trash-2 me-1"></i>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: '1rem',
                          padding: '0 1rem'
                        }}>
                          {/* Sort templates by selected criteria */}
                          {getSortedTemplates(templates)
                            .slice(0, templatesDisplayCount)
                            .map(template => {
                              // Calculate template info
                              const templateItems = Array.isArray(template.items) ? template.items : [];
                              const allCategories = templateItems.map(itemId => {
                                const item = inventoryData.find(inv => inv.id === itemId);
                                return item ? item.category : null;
                              }).filter(Boolean);
                              
                              const categoryCount = [...new Set(allCategories)].length;
                              const topCategories = [...new Set(allCategories)].slice(0, 3);
                              
                              const createdDate = template.created_at
                                ? new Date(template.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'Unknown';

                              const accentColor = getTemplateColor(template);
                              const isSelected = selectedTemplates.includes(template.id);

                              const isHighlighted = highlightTemplateId === String(template.id);

                              return (
                                <div key={template.id} data-template-id={template.id}>
                                  <div className="template-tooltip-wrapper position-relative"
                                    style={{
                                      position: 'relative',
                                      borderRadius: '10px',
                                      outline: isHighlighted ? '3px solid #C8880C' : 'none',
                                      boxShadow: isHighlighted ? '0 0 0 6px rgba(200,136,12,0.18)' : 'none',
                                      transition: 'outline 0.4s, box-shadow 0.4s',
                                    }}
                                    draggable={mode === 'count-creation'}
                                    onDragStart={(e) => {
                                      if (mode !== 'count-creation') return;
                                      e.dataTransfer.setData('application/count-template', JSON.stringify({ id: template.id, name: template.name }));
                                      e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                    onMouseEnter={(e) => {
                                      const tooltip = e.currentTarget.querySelector('.template-tooltip');
                                      if (tooltip) {
                                        tooltip.style.opacity = '1';
                                        tooltip.style.visibility = 'visible';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      const tooltip = e.currentTarget.querySelector('.template-tooltip');
                                      if (tooltip) {
                                        tooltip.style.opacity = '0';
                                        tooltip.style.visibility = 'hidden';
                                      }
                                    }}>
                                    <div className={`card position-relative ${template.order_calculations_enabled ? 'order-enabled-card' : ''}`} style={{
                                      backgroundColor: isSelected ? '#8A7A6B' : '#F5F3F0',
                                      border: isSelected ? '2px solid #5A4A42' : '1px solid #8A7A6B',
                                      borderLeft: `4px solid ${accentColor}`,
                                      minHeight: '108px',
                                      width: '100%',
                                      maxWidth: '260px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      boxShadow: '0 2px 4px rgba(90, 74, 66, 0.1)',
                                      transition: 'all 0.2s ease',
                                      margin: '0 auto',
                                      fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                      position: 'relative',
                                      zIndex: 1,
                                      overflow: 'hidden'
                                    }}>
                                    <div className="card-body py-2 px-2 d-flex flex-column" style={{ zIndex: 1 }}>
                                      <div className="position-absolute" style={{ top: '6px', right: '6px' }}>
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={selectedTemplates.includes(template.id)}
                                          onChange={() => toggleTemplateSelection(template.id)}
                                          style={{ transform: 'scale(1.05)' }}
                                        />
                                      </div>

                                      <h6 className="card-title mb-1 pe-4" style={{
                                        color: isSelected ? 'white' : accentColor,
                                        fontWeight: '700',
                                        fontSize: '0.85rem',
                                        lineHeight: '1.25',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        textOverflow: 'ellipsis',
                                        marginBottom: '0.25rem'
                                      }}>
                                        {template.name}
                                      </h6>

                                      <div className="small mb-1" style={{
                                        fontSize: '0.75rem',
                                        color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.92)' : '#5A4A42',
                                        lineHeight: 1.3
                                      }}>
                                        <span className="fw-semibold">{templateItems.length}</span>
                                        {' items'}
                                        {topCategories.length > 0 && (
                                          <>
                                            <span className="text-muted mx-1" style={{ opacity: selectedTemplates.includes(template.id) ? 0.7 : 1 }}>·</span>
                                            <span style={{ color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.85)' : '#8A7A6B' }}>
                                              {topCategories.slice(0, 2).join(', ')}
                                              {topCategories.length > 2 ? '…' : ''}
                                            </span>
                                          </>
                                        )}
                                      </div>

                                      <div className="small mt-auto mb-2" style={{
                                        fontSize: '0.68rem',
                                        color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.65)' : '#9A8B82'
                                      }}>
                                        Created {createdDate}
                                      </div>

                                      <div className="d-flex justify-content-end">
                                        <button
                                          type="button"
                                          className="btn btn-sm"
                                          style={{
                                            fontSize: '0.65rem',
                                            padding: '0.2rem 0.5rem',
                                            minHeight: '26px',
                                            lineHeight: 1,
                                            backgroundColor: mode === 'count-creation' ? '#28a745' : '#8A7A6B',
                                            border: `1px solid ${mode === 'count-creation' ? '#28a745' : '#8A7A6B'}`,
                                            color: 'white'
                                          }}
                                          title={mode === 'count-creation' ? 'Include in count' : 'Edit group'}
                                          onClick={() => {
                                            if (mode === 'count-creation') {
                                              if (window.addTemplateToCount) {
                                                window.addTemplateToCount(template);
                                              }
                                            } else {
                                              setEditingGroupForEditor(template);
                                              setStep('location-template-creator');
                                            }
                                          }}
                                        >
                                          {mode === 'count-creation' ? 'Include' : (
                                            <><i className="feather icon-edit-2" style={{ width: 14, height: 14, marginRight: 4 }} /> Edit Group</>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                    
                                    {/* Tooltip with item list */}
                                    <div className="template-tooltip" style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      backgroundColor: '#FEFCFA',
                                      color: '#5A4A42',
                                      padding: '12px 16px',
                                      borderRadius: '8px',
                                      fontSize: '0.8rem',
                                      whiteSpace: 'normal',
                                      zIndex: 1000,
                                      opacity: 0,
                                      visibility: 'hidden',
                                      transition: 'opacity 0.2s ease, visibility 0.2s ease',
                                      minWidth: '200px',
                                      maxWidth: '300px',
                                      textAlign: 'left',
                                      boxShadow: '0 4px 12px rgba(90, 74, 66, 0.15)',
                                      marginTop: '8px',
                                      pointerEvents: 'none',
                                      border: '1px solid #D4C4B0',
                                      fontFamily: "'Open Sans', sans-serif"
                                    }}>
                                      <div style={{
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        fontSize: '0.85rem',
                                        color: '#5A4A42',
                                        borderBottom: '1px solid #E8E0D8',
                                        paddingBottom: '4px'
                                      }}>
                                        Items in {template.name}:
                                      </div>
                                      <div style={{
                                        fontSize: '0.75rem',
                                        lineHeight: '1.4',
                                        color: '#8B7B73'
                                      }}>
                                        {templateItems.length > 0 ? (
                                          templateItems.slice(0, 8).map((item, idx) => (
                                            <div key={idx} style={{ marginBottom: '2px' }}>
                                              • {inventoryData.find(inv => inv.id === item)?.description || 'Unknown item'}
                                              {renderPurchaseOptionSummary(item)}
                                            </div>
                                          ))
                                        ) : (
                                          <div>No items in this template</div>
                                        )}
                                        {templateItems.length > 8 && (
                                          <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                                            ...and {templateItems.length - 8} more items
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                        
                        {/* Show More Button */}
                        {templates.length > templatesDisplayCount && (
                          <div className="text-center mt-3">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setTemplatesDisplayCount(prev => prev + 25)}
                              style={{
                                color: '#8A7A6B',
                                borderColor: '#8A7A6B'
                              }}
                            >
                              <i className="feather icon-plus me-1"></i>
                              Show more groups ({templates.length - templatesDisplayCount} remaining)
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="card" style={{
            backgroundColor: '#F5F3F0',
            border: '1px solid #D4C4B0'
          }}>
            <div className="card-header" style={{ backgroundColor: 'transparent' }}>
              <h6 className="mb-0" style={{ color: '#5A4A42' }}>Count Sessions - Chronological Order</h6>
            </div>
            <div className="card-body p-0">
              {renderChronologicalListView()}
              </div>
            </div>
          )}

        {!showCalendarView && false && (
          <>
            <div className="row mb-4 justify-content-center">
              {/* Quick Actions */}
          <div className="col-md-6 col-lg-5">
            <div className="card" style={{
              backgroundColor: '#F5F3F0',
              border: '1px solid #D4C4B0',
              minHeight: '280px'
            }}>
              <div className="card-body text-center d-flex flex-column justify-content-between">
                <div>
                  <i className="feather icon-map-pin" style={{
                    fontSize: '2.5rem',
                    color: '#8A7A6B'
                  }}></i>
                  <h5 className="mt-3 mb-2" style={{
                    color: '#5A4A42',
                    fontWeight: '600'
                  }}>What</h5>
                  <p style={{
                    color: '#5A4A42',
                    fontSize: '0.9rem',
                    lineHeight: '1.4',
                    margin: '0 0 1rem 0'
                  }}>Add items you will count</p>
                  <p style={{
                    color: '#8A7A6B',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Create templates organized by location with drag & drop sorting</p>
                </div>
                <button
                  className="btn btn-primary btn-sm w-100"
                  onClick={() => {
                    setEditingGroupForEditor(null);
                    setStep('location-template-creator');
                  }}
                >
                  <i className="feather icon-map-pin me-1"></i>
                  New group
                </button>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-5">
            <div className="card" style={{
              backgroundColor: '#F5F3F0',
              border: '1px solid #D4C4B0',
              minHeight: '280px'
            }}>
              <div className="card-body text-center d-flex flex-column justify-content-between">
                <div>
                  <i className="feather icon-calendar" style={{
                    fontSize: '2.5rem',
                    color: '#8A7A6B'
                  }}></i>
                  <h5 className="mt-3 mb-2" style={{
                    color: '#5A4A42',
                    fontWeight: '600'
                  }}>When</h5>
                  <p style={{
                    color: '#5A4A42',
                    fontSize: '0.9rem',
                    lineHeight: '1.4',
                    margin: '0 0 1rem 0'
                  }}>Add items and templates to count together</p>
                  <p style={{
                    color: '#8A7A6B',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Schedule a count using existing templates with specific date/time</p>
                </div>
                <div>
                <button
                    className="btn btn-primary btn-sm w-100"
                  onClick={() => {
                    // Clear all filters when starting fresh session creation
                    setItemSearchTerm('');
                    setItemCategoryFilter('');
                    setItemVendorFilter('');
                    setItemLocationFilter('');
                    setSelectedItems([]);
                    setSelectedTemplatesForSession([]);
                    setStep('create-session');
                  }}
                  disabled={templates.length === 0}
                >
                  <i className="feather icon-clock me-1"></i>
                  New Session
                </button>
                {!loading && templates.length === 0 && (
                    <small style={{
                      color: '#8A7A6B',
                      fontSize: '0.75rem',
                      display: 'block',
                      marginTop: '0.5rem'
                    }}>Create a template first</small>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Active Count Sessions */}
        <div className="row mb-4">
          <div className="col-12">
            <h6>Active Count Sessions ({countSessions.filter(s => s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused').length})</h6>
            {countSessions.filter(s => s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused').length === 0 ? (
              <div className="alert alert-info">
                <p className="mb-0">No active count sessions. Create a new count session to get started.</p>
              </div>
            ) : (
              <div className="row">
                {countSessions.filter(s => s.status === 'draft' || s.status === 'in_progress' || s.status === 'paused').map(session => (
                  <div key={session.id} className="col-md-6 mb-3">
                    <div className="card border-primary">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="card-title mb-0">{session.name}</h6>
                          <span className={`badge ${session.status === 'draft' ? 'bg-secondary' : 'bg-warning'}`}>
                            {session.status === 'draft' ? 'Not Started' : session.status === 'paused' ? 'Saved' : 'In Progress'}
                          </span>
                        </div>
                        <p className="card-text small text-muted">
                          <i className="feather icon-calendar me-1"></i>
                          {session.session_date} at {session.count_commence_time}
                        </p>
                        <p className="small text-info mb-2">
                          <i className="feather icon-check-square me-1"></i>
                          {(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items counted` : c > 0 ? `${c} items counted` : '0 items counted'; })()}
                        </p>
                        <button
                          className="btn btn-primary btn-sm w-100"
                          onClick={() => window.location.href = 'count.html'}
                        >
                          <i className="feather icon-play me-1"></i>
                          {session.status === 'draft' ? 'Start Counting' : 'Resume Counting'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Completed Count Sessions */}
        <div className="row mb-4">
          <div className="col-12">
            <h6>Recently Completed Sessions ({completedSessions.length})</h6>
            {completedSessions.length === 0 ? (
              <div className="alert alert-secondary">
                <p className="mb-0">No completed count sessions yet.</p>
              </div>
            ) : (
              <div className="row">
                {completedSessions.map(session => (
                  <div key={session.id} className="col-md-6 mb-3">
                    <div className="card border-success">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="card-title mb-0">{session.name}</h6>
                          <span className="badge bg-success">Completed</span>
                        </div>
                        <p className="card-text small text-muted">
                          <i className="feather icon-calendar me-1"></i>
                          {session.session_date} at {session.count_commence_time}
                        </p>
                        <p className="small text-success mb-2">
                          <i className="feather icon-check-circle me-1"></i>
                          {(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items counted` : c > 0 ? `${c} items counted` : '0 items counted'; })()}
                        </p>
                        <p className="small text-muted mb-2">
                          <i className="feather icon-clock me-1"></i>
                          Completed: {session.completed_at ? new Date(session.completed_at).toLocaleDateString() : 'N/A'}
                        </p>
                        <button
                          className="btn btn-outline-success btn-sm w-100"
                          onClick={() => {
                            // TODO: Add view report functionality
                            alert('Report viewing functionality coming soon!');
                          }}
                        >
                          <i className="feather icon-file-text me-1"></i>
                          View Report
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Template Editor Section */}
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Manage Item Groups</h6>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-info btn-sm"
                  onClick={() => setShowArchiveView(true)}
                >
                  <i className="feather icon-archive me-1"></i>
                  View Archive
                </button>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: '#2e7d32', borderColor: '#2e7d32', color: '#fff' }}
                  onClick={() => {
                    setEditingGroupForEditor(null);
                    setStep('location-template-creator');
                  }}
                >
                  <i className="feather icon-plus me-1"></i>
                  New Item Group
                </button>
              </div>
            </div>
            
            {/* Template Management Controls */}
            {templates.length > 0 && (
              <div className="mb-3 p-3" style={{ backgroundColor: '#F8F9FA', borderRadius: '8px', border: '1px solid #DEE2E6' }}>
                <div className="d-flex justify-content-between align-items-center" style={{
                  fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
                  fontSize: '16px',
                  fontWeight: '400',
                  color: 'rgb(33, 37, 41)',
                  padding: '16px',
                  marginBottom: '16px',
                  marginTop: '0',
                  marginLeft: '0',
                  marginRight: '0'
                }}>
                  <div className="d-flex align-items-center gap-3" style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: '16px',
                    fontWeight: '400',
                    color: '#5A4A42',
                    padding: '0',
                    margin: '0'
                  }}>

                    {/* Custom Filter Dropdown */}
                    <div className="custom-dropdown template-filter-dropdown" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setTemplateFilterDropdownOpen(!templateFilterDropdownOpen)}
                        style={{
                          fontSize: '0.9rem',
                          border: '1px solid #D4C4B0',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          background: '#fff',
                          color: '#5A4A42',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'border-color 0.2s ease',
                          fontFamily: "'Open Sans', sans-serif",
                          minWidth: '160px'
                        }}
                        onMouseEnter={(e) => e.target.style.borderColor = '#C8B5A0'}
                        onMouseLeave={(e) => e.target.style.borderColor = '#D4C4B0'}
                      >
                        <i className="feather icon-filter"></i>
                        Filter: {getFilterDescription()}
                        <i className="feather icon-chevron-down" style={{ marginLeft: 'auto' }}></i>
                      </button>

                      {templateFilterDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          background: '#fff',
                          border: '1px solid #D4C4B0',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(90, 74, 66, 0.1)',
                          zIndex: 1000,
                          marginTop: '2px',
                          fontFamily: "'Open Sans', sans-serif",
                          minWidth: '200px'
                        }}>
                          {/* Status Filter Section */}
                          <div style={{ padding: '8px 12px', borderBottom: '1px solid #F0F0F0', fontSize: '0.8rem', fontWeight: 'bold', color: '#8B7B73' }}>
                            Archive Status
                          </div>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, status: 'all' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.status === 'all' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'all' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-list"></i>
                            All groups
                          </button>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, status: 'active' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.status === 'active' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'active' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-check-circle"></i>
                            Active Only
                          </button>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, status: 'archived' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.status === 'archived' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.status === 'archived' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-archive"></i>
                            Archived Only
                          </button>

                          {/* Order Type Filter Section */}
                          <div style={{ padding: '8px 12px', borderBottom: '1px solid #F0F0F0', borderTop: '1px solid #F0F0F0', fontSize: '0.8rem', fontWeight: 'bold', color: '#8B7B73' }}>
                            Order Type
                          </div>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, orderType: 'all' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.orderType === 'all' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'all' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-layers"></i>
                            All Types
                          </button>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, orderType: 'order' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.orderType === 'order' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'order' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-shopping-cart"></i>
                            Order & Count
                          </button>
                          <button
                            onClick={() => {
                              setTemplateFilters(prev => ({ ...prev, orderType: 'count-only' }));
                              setTemplateFilterDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateFilters.orderType === 'count-only' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateFilters.orderType === 'count-only' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-check-square"></i>
                            Count Only
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Custom Sort Dropdown */}
                    <div className="custom-dropdown template-sort-dropdown" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setTemplateSortDropdownOpen(!templateSortDropdownOpen)}
                        style={{
                          fontSize: '0.9rem',
                          border: '1px solid #D4C4B0',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          background: '#fff',
                          color: '#5A4A42',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'border-color 0.2s ease',
                          fontFamily: "'Open Sans', sans-serif",
                          minWidth: '160px'
                        }}
                        onMouseEnter={(e) => e.target.style.borderColor = '#C8B5A0'}
                        onMouseLeave={(e) => e.target.style.borderColor = '#D4C4B0'}
                      >
                        <i className="feather icon-sort"></i>
                        Sort: {templateSortBy === 'name' ? 'Name' : templateSortBy === 'created_at' ? 'Created' : 'Last Used'}
                        <i className="feather icon-chevron-down" style={{ marginLeft: 'auto' }}></i>
                      </button>
                      
                      {templateSortDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          background: '#fff',
                          border: '1px solid #D4C4B0',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(90, 74, 66, 0.1)',
                          zIndex: 1000,
                          marginTop: '2px',
                          fontFamily: "'Open Sans', sans-serif",
                          minWidth: '160px'
                        }}>
                          <button
                            onClick={() => {
                              setTemplateSortBy('name');
                              setTemplateSortDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateSortBy === 'name' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateSortBy === 'name' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-type"></i>
                            Sort by Name
                          </button>
                          <button
                            onClick={() => {
                              setTemplateSortBy('created_at');
                              setTemplateSortDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateSortBy === 'created_at' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateSortBy === 'created_at' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-calendar"></i>
                            Sort by Date Created
                          </button>
                          <button
                            onClick={() => {
                              setTemplateSortBy('last_used');
                              setTemplateSortDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: 'none',
                              background: templateSortBy === 'last_used' ? '#FAF8F6' : 'transparent',
                              color: '#5A4A42',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#FAF8F6'}
                            onMouseLeave={(e) => e.target.style.background = templateSortBy === 'last_used' ? '#FAF8F6' : 'transparent'}
                          >
                            <i className="feather icon-clock"></i>
                            Sort by Last Used
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {selectedTemplates.length > 0 && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => {
                          setTemplatesToArchive(selectedTemplates);
                          setShowArchiveModal(true);
                        }}
                      >
                        <i className="feather icon-archive me-1"></i>
                        Archive ({selectedTemplates.length})
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          if (selectedTemplates.length === 1) {
                            handleRequestDeleteTemplate(selectedTemplates[0]);
                          } else {
                            alert('Please select only one template to delete at a time.');
                          }
                        }}
                        disabled={selectedTemplates.length !== 1}
                      >
                        <i className="feather icon-trash-2 me-1"></i>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!loading && templates.length === 0 ? (
              <div className="alert alert-info">
                <p className="mb-0">No groups yet. Create a group to define reusable item lists for counting.</p>
              </div>
            ) : (
              <>
              <div 
                className="template-grid-container"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: '1rem',
                  padding: '0 1rem',
                  overflowX: 'visible',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#8A7A6B #F5F3F0',
                  position: 'relative',
                  zIndex: 1,
                  contain: 'layout style paint'
                }}
              >
                  {/* Sort templates by selected criteria */}
                  {getSortedTemplates(templates)
                    .slice(0, templatesDisplayCount)
                    .map(template => {
                    // Get template items for analysis
                    const templateItems = Array.isArray(template.items) 
                      ? inventoryData.filter(item => template.items.includes(item.id))
                      : [];
                    
                    // Get top 3 categories
                    const categoryCount = {};
                    templateItems.forEach(item => {
                      if (item.category) {
                        categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
                      }
                    });
                    const topCategories = Object.entries(categoryCount)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 3)
                      .map(([category]) => category);

                    // Format created date
                    const createdDate = template.created_at
                      ? new Date(template.created_at).toLocaleDateString()
                      : 'Unknown';

                    const accentColor = getTemplateColor(template);
                    const isSelected = selectedTemplates.includes(template.id);

                    return (
                      <div key={template.id}>
                        <div className="template-tooltip-wrapper position-relative"
                          style={{ position: 'relative' }}
                          draggable={mode === 'count-creation'}
                          onDragStart={(e) => {
                            if (mode !== 'count-creation') return;
                            e.dataTransfer.setData('application/count-template', JSON.stringify({ id: template.id, name: template.name }));
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onMouseEnter={(e) => {
                            const tooltip = e.currentTarget.querySelector('.template-tooltip');
                            if (tooltip) {
                              tooltip.style.opacity = '1';
                              tooltip.style.visibility = 'visible';
                            }
                          }}
                          onMouseLeave={(e) => {
                            const tooltip = e.currentTarget.querySelector('.template-tooltip');
                            if (tooltip) {
                              tooltip.style.opacity = '0';
                              tooltip.style.visibility = 'hidden';
                            }
                          }}>
                          <div className={`card position-relative ${template.order_calculations_enabled ? 'order-enabled-card' : ''}`} style={{
                            backgroundColor: isSelected ? '#8A7A6B' : '#F5F3F0',
                            border: isSelected ? '2px solid #5A4A42' : '1px solid #8A7A6B',
                            borderLeft: `4px solid ${accentColor}`,
                            minHeight: '108px',
                            width: '100%',
                            maxWidth: '260px',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 2px 4px rgba(90, 74, 66, 0.1)',
                            transition: 'all 0.2s ease',
                            margin: '0 auto',
                            fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            position: 'relative',
                            zIndex: 'auto',
                            overflow: 'hidden'
                          }}>
                          <div className="card-body py-2 px-2 d-flex flex-column" style={{ zIndex: 1 }}>
                            <div className="position-absolute" style={{ top: '6px', right: '6px' }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedTemplates.includes(template.id)}
                                onChange={() => toggleTemplateSelection(template.id)}
                                style={{ transform: 'scale(1.05)' }}
                              />
                            </div>

                            <h6 className="card-title mb-1 pe-4" style={{
                              color: isSelected ? 'white' : accentColor,
                              fontWeight: '700',
                              fontSize: '0.85rem',
                              lineHeight: '1.25',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              textOverflow: 'ellipsis',
                              marginBottom: '0.25rem'
                            }}>
                              {template.name}
                            </h6>

                            <div className="small mb-1" style={{
                              fontSize: '0.72rem',
                              color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.92)' : '#5A4A42',
                              lineHeight: 1.3
                            }}>
                              <span className="fw-semibold">{templateItems.length}</span>
                              {' items'}
                              {topCategories.length > 0 && (
                                <>
                                  <span className="text-muted mx-1" style={{ opacity: selectedTemplates.includes(template.id) ? 0.7 : 1 }}>·</span>
                                  <span style={{ color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.85)' : '#8A7A6B' }}>
                                    {topCategories.slice(0, 2).join(', ')}
                                    {topCategories.length > 2 ? '…' : ''}
                                  </span>
                                </>
                              )}
                            </div>

                            <div className="small mt-auto mb-2" style={{
                              fontSize: '0.68rem',
                              color: selectedTemplates.includes(template.id) ? 'rgba(255,255,255,0.65)' : '#9A8B82'
                            }}>
                              Created {createdDate}
                            </div>

                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-sm"
                                style={{
                                  fontSize: '0.65rem',
                                  padding: '0.2rem 0.5rem',
                                  minHeight: '26px',
                                  lineHeight: 1,
                                  backgroundColor: mode === 'count-creation' ? '#28a745' : 'rgb(138, 122, 107)',
                                  border: `1px solid ${mode === 'count-creation' ? '#28a745' : 'rgb(138, 122, 107)'}`,
                                  color: 'white'
                                }}
                                title={mode === 'count-creation' ? 'Include in count' : 'Edit group'}
                                onClick={() => {
                                  if (mode === 'count-creation') {
                                    if (window.addTemplateToCount) {
                                      window.addTemplateToCount(template);
                                    }
                                  } else {
                                    setEditingGroupForEditor(template);
                                    setStep('location-template-creator');
                                  }
                                }}
                              >
                                {mode === 'count-creation' ? 'Include' : (
                                  <><i className="feather icon-edit-2" style={{ width: 14, height: 14, marginRight: 4 }} /> Edit Group</>
                                )}
                              </button>
                            </div>
                          </div>
                    </div>
                      
                      {/* Tooltip with item list */}
                      <div className="template-tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#FEFCFA',
                        color: '#5A4A42',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        whiteSpace: 'normal',
                        zIndex: 1000,
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s ease, visibility 0.2s ease',
                        minWidth: '200px',
                        maxWidth: '300px',
                        textAlign: 'left',
                        boxShadow: '0 4px 12px rgba(90, 74, 66, 0.15)',
                        marginTop: '8px',
                        pointerEvents: 'none',
                        border: '1px solid #D4C4B0',
                        fontFamily: "'Open Sans', sans-serif"
                      }}>
                        <div style={{
                          fontWeight: '600',
                          marginBottom: '8px',
                          fontSize: '0.85rem',
                          color: '#5A4A42',
                          borderBottom: '1px solid #E8E0D8',
                          paddingBottom: '4px'
                        }}>
                          Items in {template.name}:
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          lineHeight: '1.4',
                          color: '#8B7B73'
                        }}>
                          {templateItems.length > 0 ? (
                            templateItems.slice(0, 8).map((item, idx) => (
                              <div key={idx} style={{ marginBottom: '2px' }}>
                                • {item?.description || 'Unknown item'}
                                {renderPurchaseOptionSummary(item?.id)}
                              </div>
                            ))
                          ) : (
                            <div>No items in this template</div>
                          )}
                          {templateItems.length > 8 && (
                            <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                              ...and {templateItems.length - 8} more items
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    );
                  })}
                </div>
                
                {/* Show More Button */}
                {templates.length > templatesDisplayCount && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setTemplatesDisplayCount(prev => prev + 25)}
                      style={{
                        color: '#8A7A6B',
                        borderColor: '#8A7A6B'
                      }}
                    >
                      <i className="feather icon-plus me-1"></i>
                      Show more groups ({templates.length - templatesDisplayCount} remaining)
                    </button>
              </div>
                )}
              </>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </div>
    </>
    );
  };

  const renderCreateSessionStep = () => (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">{isEditMode ? 'Edit Count Session' : 'Create Count Session'}</h5>
        <p className="text-muted mb-0">
          {isEditMode 
            ? 'Modify count session details and edit items' 
            : 'Schedule a count using templates with specific date and time'
          }
        </p>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label">Session Name</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={countSession.name || ''}
                onChange={(e) => setCountSession(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter session name..."
                autoComplete="off"
                data-lpignore="true"
                style={{
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  const newName = generateSessionName();
                  setCountSession(prev => ({ ...prev, name: newName }));
                }}
                title="Auto-generate name"
              >
                <i className="feather icon-refresh-cw"></i>
              </button>
            </div>
            <small className="text-muted">
              <i className="feather icon-edit me-1"></i>
              Enter a custom name or click <i className="feather icon-refresh-cw"></i> to auto-generate
            </small>
          </div>
          <div className="col-md-6">
            <label className="form-label">Session Date</label>
            <input
              type="date"
              className="form-control"
              value={countSession.session_date}
              onChange={(e) => setCountSession(prev => ({ ...prev, session_date: e.target.value }))}
              autoComplete="off"
              data-lpignore="true"
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label">Created Time</label>
            <input
              type="text"
              className="form-control"
              value={countSession.created_time}
              readOnly
              autoComplete="off"
              data-lpignore="true"
              tabIndex="-1"
              style={{ backgroundColor: '#f8f9fa' }}
            />
            <small className="text-muted">When session was created</small>
          </div>
          <div className="col-md-4">
            <label className="form-label">Count Commence Time</label>
            <input
              type="time"
              className="form-control"
              value={convertTo24Hour(countSession.count_commence_time)}
              onChange={(e) => {
                const time24 = e.target.value;
                const time12 = convertTo12Hour(time24);
                setCountSession(prev => ({ ...prev, count_commence_time: time12 }));
              }}
              autoComplete="off"
              data-lpignore="true"
            />
            <small className="text-muted">Start time: {countSession.count_commence_time}</small>
          </div>
          <div className="col-md-4">
            <label className="form-label">Timezone</label>
            <input
              type="text"
              className="form-control"
              value={userTimezone}
              readOnly
              autoComplete="off"
              data-lpignore="true"
              tabIndex="-1"
              style={{ backgroundColor: '#f8f9fa' }}
            />
            <small className="text-muted">Auto-detected</small>
          </div>
        </div>

        {/* Order Mode Toggle */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="d-flex align-items-center gap-3 p-3" style={{ backgroundColor: sessionOrderMode ? '#E8F5E9' : '#F5F3F0', borderRadius: '8px', border: `2px solid ${sessionOrderMode ? '#4CAF50' : '#D4C4B0'}`, transition: 'all 0.2s' }}>
              <div className="form-check form-switch" style={{ marginBottom: 0 }}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="orderModeToggle"
                  checked={sessionOrderMode}
                  onChange={(e) => setSessionOrderMode(e.target.checked)}
                  style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                />
              </div>
              <div>
                <label className="form-check-label fw-bold" htmlFor="orderModeToggle" style={{ cursor: 'pointer', color: '#5A4A42', fontSize: '0.95rem' }}>
                  {sessionOrderMode ? '📦 Order Mode: ON' : '📋 Count Only Mode'}
                </label>
                <div style={{ fontSize: '0.8rem', color: '#8B7B73' }}>
                  {sessionOrderMode
                    ? 'Counted items will generate order suggestions based on PAR levels'
                    : 'Count inventory only — no order calculations'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-12">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-control"
              rows="2"
              value={countSession.notes}
              onChange={(e) => setCountSession(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this count session..."
              autoComplete="off"
              data-lpignore="true"
            />
          </div>
        </div>

        {/* 🎯 UNIFIED EDITOR - 50/50 Split Layout */}
        <div className="row mb-4">
          
          {/* ========== LEFT 50%: TEMPLATE SELECTION ========== */}
              <div className="col-md-6">
            <div className="card h-100" style={{ 
              backgroundColor: '#F8F9FA', 
              border: '1px solid #DEE2E6',
              position: 'relative',
              zIndex: 1,
              overflow: 'hidden'
            }}>
              <div className="card-header" style={{ backgroundColor: 'transparent', borderBottom: '1px solid #DEE2E6' }}>
                <h6 className="mb-0" style={{ color: '#5A4A42' }}>
                  <i className="feather icon-layers me-2"></i>
                  Templates ({selectedTemplatesForSession.length} selected)
                </h6>
                <small className="text-muted">Select templates to include all their items</small>
              </div>
              <div className="card-body p-3">
                
                {/* Selected Templates Summary */}
                {selectedTemplatesForSession.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-1">
                      {selectedTemplatesForSession.map(template => {
                        const accentColor = getTemplateColor(template);
                        return (
                          <span
                            key={template.id}
                            className="badge d-flex align-items-center gap-1"
                            style={{ backgroundColor: accentColor, color: '#fff' }}
                          >
                            {template.name} ({Array.isArray(template.items) ? template.items.length : 0})
                            <button
                              className="btn btn-sm p-0 text-white"
                              style={{ background: 'none', border: 'none', fontSize: '12px' }}
                              onClick={() => {
                                setSelectedTemplatesForSession(prev =>
                                  prev.filter(t => t.id !== template.id)
                                );
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available Templates */}
                <div style={{ maxHeight: isEditMode ? '400px' : '350px', overflowY: 'auto' }}>
                  {!isEditMode && !loading && templates.length === 0 ? (
                  <div className="alert alert-info">
                      <p className="mb-0 small">No templates available. Create templates first.</p>
                  </div>
                  ) : !isEditMode && templates.filter(t => !selectedTemplatesForSession.find(st => st.id === t.id)).length === 0 ? (
                    <div className="alert alert-success">
                      <p className="mb-0 small">All available templates are selected!</p>
                    </div>
                  ) : !isEditMode ? (
                    templates
                      .filter(t => !selectedTemplatesForSession.find(st => st.id === t.id))
                      .map(template => {
                        const accentColor = getTemplateColor(template);
                        return (
                        <div key={template.id} className="template-tooltip-wrapper position-relative">
                          <div
                            className="card mb-2"
                            style={{
                              cursor: 'pointer',
                              backgroundColor: '#F5F3F0',
                              border: '1px solid #8A7A6B',
                              borderLeft: `4px solid ${accentColor}`,
                              transition: '0.2s'
                            }}
                          >
                          <div className="card-body p-2">
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2"
                                  style={{ transform: 'scale(1.2)' }}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedTemplatesForSession(prev => [...prev, template]);
                                      if (template.order_calculations_enabled) setSessionOrderMode(true);
                                    }
                                  }}
                                />
                                <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                      <strong className="small" style={{ color: accentColor }}>{template.name}</strong><br />
                                      <small style={{ color: '#8A7A6B' }}>
                                        {template.category} | <strong>{Array.isArray(template.items) ? template.items.length : 0}</strong> items
                                </small>
                              </div>
                              <button
                                      className="btn btn-sm"
                                      style={{
                                        backgroundColor: '#5A4A42',
                                        border: '1px solid #5A4A42',
                                        color: 'white',
                                        fontSize: '0.6rem',
                                        padding: '0.25rem 0.5rem'
                                      }}
                                onClick={() => {
                                  setSelectedTemplatesForSession(prev => [...prev, template]);
                                  if (template.order_calculations_enabled) setSessionOrderMode(true);
                                }}
                              >
                                      Use
                              </button>
                            </div>
                          </div>
                        </div>
                  </div>
              </div>

                          {/* Template Tooltip */}
                          <div
                            className="template-tooltip"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#FEFCFA',
                              color: '#5A4A42',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              whiteSpace: 'normal',
                              zIndex: 1000,
                              opacity: 0,
                              visibility: 'hidden',
                              transition: 'opacity 0.2s ease, visibility 0.2s ease',
                              minWidth: '200px',
                              maxWidth: '300px',
                              textAlign: 'left',
                              boxShadow: '0 4px 12px rgba(90, 74, 66, 0.15)',
                              marginTop: '8px',
                              pointerEvents: 'none',
                              border: '1px solid #D4C4B0',
                              fontFamily: "'Open Sans', sans-serif"
                            }}
                          >
                            <div style={{
                              fontWeight: '600',
                              marginBottom: '8px',
                              fontSize: '0.85rem',
                              color: '#5A4A42',
                              borderBottom: '1px solid #E8E0D8',
                              paddingBottom: '4px'
                            }}>
                              Items in {template.name}:
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              lineHeight: '1.4',
                              color: '#8B7B73'
                            }}>
                              {Array.isArray(template.items) && template.items.length > 0 ? (
                                <>
                                  {inventoryData
                                    .filter(item => template.items.includes(item.id))
                                    .slice(0, 8)
                                    .map((item, index) => (
                                      <div key={index} style={{ marginBottom: '2px' }}>
                                        • {item.description}
                      </div>
                    ))}
                                  {template.items.length > 8 && (
                                    <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                                      ...and {template.items.length - 8} more items
                  </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ fontStyle: 'italic' }}>No items in this template</div>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })
                  ) : (
                    <div className="alert alert-info">
                      <p className="mb-0 small">
                        <i className="feather icon-edit me-1"></i>
                        Template selection is disabled in edit mode. Focus on individual items.
                      </p>
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>

          {/* ========== RIGHT 50%: INDIVIDUAL ITEM SELECTION ========== */}
          <div className="col-md-6">
            <div className="card h-100" style={{ 
              backgroundColor: '#F8F9FA', 
              border: '1px solid #DEE2E6',
              position: 'relative',
              zIndex: 2
            }}>
              <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'transparent', borderBottom: '1px solid #DEE2E6' }}>
                <div>
                  <h6 className="mb-0" style={{ color: '#5A4A42' }}>
                    <i className="feather icon-package me-2"></i>
                    Individual Items ({selectedItems.length} selected)
                  </h6>
                  <small className="text-muted">
                    {isEditMode ? 'Add or remove items from this session' : 'Add specific items not in templates'}
                  </small>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowDatabaseBrowser(true)}
                  title="Open full database browser to add items"
                >
                  <i className="feather icon-edit me-1"></i>
                  Edit Items
                </button>
            </div>
              <div className="card-body p-3">

                {/* Item Filters - Compact Layout */}
                <div className="row g-2 mb-3">
                  <div className="col-12">
                <input
                  type="text"
                  className="form-control form-control-sm"
                      placeholder="🔍 Search items by name..."
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  onInput={(e) => setItemSearchTerm(e.target.value)}
                  autoComplete="off"
                  data-lpignore="true"
                />
              </div>
                  <div className="col-4">
                <select
                  className="form-control form-control-sm"
                  value={itemCategoryFilter}
                  onChange={(e) => setItemCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {getUniqueValues('category').map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
                  <div className="col-4">
                <select
                  className="form-control form-control-sm"
                  value={itemVendorFilter}
                  onChange={(e) => setItemVendorFilter(e.target.value)}
                >
                  <option value="">All Vendors</option>
                  {getUniqueValues('brand').map(vendor => (
                    <option key={vendor} value={vendor}>{vendor}</option>
                  ))}
                </select>
              </div>
                  <div className="col-4">
                <select
                  className="form-control form-control-sm"
                  value={itemLocationFilter}
                  onChange={(e) => setItemLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {getUniqueValues('location').map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
            </div>

                {/* Selected Items Summary */}
                {selectedItems.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-1">
                      {selectedItems.slice(0, 5).map(item => (
                        <span key={item.id} className="badge bg-primary d-flex align-items-center gap-1">
                          {item.description.substring(0, 15)}...
                          <button
                            className="btn btn-sm p-0 text-white"
                            style={{ background: 'none', border: 'none', fontSize: '12px' }}
                            onClick={() => {
                              setSelectedItems(prev => prev.filter(si => si.id !== item.id));
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {selectedItems.length > 5 && (
                        <span className="badge bg-secondary">+{selectedItems.length - 5} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Available Items List */}
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {getFilteredItems().map(item => (
                    <div 
                      key={item.id} 
                      className="card mb-2" 
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: '#F5F3F0',
                        border: '1px solid #8A7A6B',
                        transition: '0.2s',
                        boxShadow: selectedItems.find(si => si.id === item.id) 
                          ? '0px 2px 4px rgba(90, 74, 66, 0.2)' 
                          : '0px 1px 2px rgba(90, 74, 66, 0.1)'
                      }}
                    >
                      <div className="card-body p-2">
                        <div className="d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input me-2"
                            style={{ transform: 'scale(1.2)' }}
                            checked={!!selectedItems.find(si => si.id === item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems(prev => [...prev, item]);
                              } else {
                                setSelectedItems(prev => prev.filter(si => si.id !== item.id));
                              }
                            }}
                          />
                          <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                                <strong className="small" style={{ color: '#5A4A42' }}>{item.description}</strong><br />
                                <small style={{ color: '#8A7A6B' }}>
                              {item.brand} | {item.category} | {item.location}
                            </small>
                          </div>
                          <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: selectedItems.find(si => si.id === item.id) 
                                    ? '#8A7A6B' 
                                    : '#5A4A42',
                                  border: selectedItems.find(si => si.id === item.id) 
                                    ? '1px solid #8A7A6B' 
                                    : '1px solid #5A4A42',
                                  color: 'white',
                                  fontSize: '0.6rem',
                                  padding: '0.25rem 0.5rem'
                                }}
                            onClick={() => {
                                  if (selectedItems.find(si => si.id === item.id)) {
                                    setSelectedItems(prev => prev.filter(si => si.id !== item.id));
                                  } else {
                              setSelectedItems(prev => [...prev, item]);
                                  }
                            }}
                          >
                            {selectedItems.find(si => si.id === item.id) ? (
                              <i className="feather icon-check"></i>
                            ) : (
                              <i className="feather icon-plus"></i>
                            )}
                          </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {getFilteredItems().length === 0 && (
                    <div className="alert alert-info">
                      <p className="mb-0 small">
                        No items match your filters.
                        {inventoryData.length === 0 && " (No inventory data loaded)"}
                        {inventoryData.length > 0 && selectedTemplatesForSession.length > 0 && " (All items may be in selected templates)"}
                      </p>
                      {inventoryData.length > 0 && (
                        <button
                          className="btn btn-outline-primary btn-sm mt-2"
                          onClick={() => {
                            setItemSearchTerm('');
                            setItemCategoryFilter('');
                            setItemVendorFilter('');
                            setItemLocationFilter('');
                          }}
                        >
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
                    </div>
                            </div>
                          </div>

        {/* Summary Information */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-info d-flex align-items-center">
              <i className="feather icon-info me-2"></i>
              <div className="flex-grow-1">
                <strong>Total Items for Session:</strong> {(() => {
                  const templateItems = selectedTemplatesForSession.reduce((acc, template) => {
                    return acc + (Array.isArray(template.items) ? template.items.length : 0);
                  }, 0);
                  return templateItems + selectedItems.length;
                })()} items
                {selectedTemplatesForSession.length > 0 && (
                  <span className="ms-2">
                    ({selectedTemplatesForSession.reduce((acc, template) => {
                      return acc + (Array.isArray(template.items) ? template.items.length : 0);
                    }, 0)} from templates + {selectedItems.length} individual)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <button className="btn btn-secondary" onClick={() => setStep('dashboard')}>
            <i className="feather icon-arrow-left me-1"></i>
            Back to Dashboard
          </button>
          
          {/* Delete button - Only show in edit mode for sessions with no counted items */}
          {isEditMode && editingSessionId && (
            <button 
              className="btn btn-outline-danger"
              onClick={() => handleDeleteSession(editingSessionId)}
              disabled={loading}
              title="Delete this count session permanently"
            >
              <i className="feather icon-trash-2 me-1"></i>
              Delete Session
            </button>
          )}
          
          <button
            className="btn btn-primary"
            onClick={async () => {
              // Check if any items are selected
              const totalTemplateItems = selectedTemplatesForSession.reduce((acc, template) => {
                return acc + (Array.isArray(template.items) ? template.items.length : 0);
              }, 0);
              const totalItems = totalTemplateItems + selectedItems.length;
              
              if (totalItems === 0) {
                alert('Please select at least one template or individual items');
                return;
              }
              
              try {
                setLoading(true);
                
                // Build a lookup from inventoryData for fast access by ID
                const inventoryLookup = {};
                inventoryData.forEach(item => { inventoryLookup[item.id] = item; });

                // Collect per-(inventory_item_id, location) template rows.
                // Each count_template_items row may place the same inventory item in a
                // different location with its own par, so we must NOT collapse them by
                // inventory_item_id. Dedupe only by the composite (id, location) key.
                const templateLocationRows = [];
                const templateSeenKeys = new Set();

                for (const template of selectedTemplatesForSession) {
                  const { data: ctiRows, error: ctiError } = await supabase
                    .from('count_template_items')
                    .select('inventory_item_id, par_level, par_unit, par_defaulted, location')
                    .eq('template_id', template.id);

                  if (!ctiError && ctiRows && ctiRows.length > 0) {
                    ctiRows.forEach(row => {
                      const invItem = inventoryLookup[row.inventory_item_id];
                      if (!invItem) return; // item removed from inventory
                      const loc = row.location || invItem.location || null;
                      const key = `${row.inventory_item_id}::${loc || ''}`;
                      if (!templateSeenKeys.has(key)) {
                        templateSeenKeys.add(key);
                        templateLocationRows.push({ ...row, location: loc });
                      }
                    });
                  } else if (Array.isArray(template.items)) {
                    // Fallback: template items query failed — use old single-location path
                    template.items.forEach(itemId => {
                      const invItem = inventoryLookup[itemId];
                      if (!invItem) return;
                      const loc = invItem.location || null;
                      const key = `${itemId}::${loc || ''}`;
                      if (!templateSeenKeys.has(key)) {
                        templateSeenKeys.add(key);
                        templateLocationRows.push({
                          inventory_item_id: itemId,
                          location: loc,
                          par_level: invItem.par_level ?? null,
                          par_unit: invItem.unit || 'each',
                          par_defaulted: invItem.par_defaulted === true,
                        });
                      }
                    });
                  }
                }

                // Individually selected items that aren't already covered by a template row
                const extraItems = [];
                selectedItems.forEach(item => {
                  const key = `${item.id}::${item.location || ''}`;
                  if (!templateSeenKeys.has(key)) {
                    templateSeenKeys.add(key);
                    extraItems.push(item);
                  }
                });

                // Unified count for the session header
                const uniqueItems = { length: templateLocationRows.length + extraItems.length };
                
                // Get user and store info from localStorage
                const storedUser = localStorage.getItem('currentUser');
                const storedStoreId = localStorage.getItem('selectedStoreId');

                if (!storedUser || !storedStoreId) {
                  alert('You must be logged in to create count sessions');
                  return;
                }

                const user = JSON.parse(storedUser);
                const storeId = parseInt(storedStoreId);
                console.log('✅ [CountSessionManager] Creating count session for user:', user.email, 'store:', storeId);
                
                // Use explicit toggle, falling back to template setting
                const orderCalculationsEnabled = sessionOrderMode;
                
                // Create the count session in database
                const sessionData = {
                  store_id: storeId,
                  created_by: user.id,
                  name: countSession.name,
                  template_id: selectedTemplatesForSession[0]?.id || null,
                  template_name_snapshot: selectedTemplatesForSession[0]?.name || null,
                  template_type_snapshot: selectedTemplatesForSession[0]?.category || null,
                  session_date: countSession.session_date,
                  created_time: countSession.created_time,
                  count_commence_time: countSession.count_commence_time,
                  user_timezone: userTimezone,
                  notes: countSession.notes,
                  status: 'draft',
                  total_items: uniqueItems.length,
                  items_counted: 0,
                  order_calculations_enabled: orderCalculationsEnabled
                };

                const { data: sessionResult, error: sessionError } = await supabase
                  .from('count_sessions')
                  .insert([sessionData])
                  .select()
                  .single();

                if (sessionError) {
                  console.error('Error creating count session:', sessionError);
                  console.error('Error details:', JSON.stringify(sessionError, null, 2));
                  alert('Error creating count session: ' + (sessionError.message || sessionError.details || JSON.stringify(sessionError)));
                  return;
                }

                // Build count_items rows — one per (inventory_item_id, location) pair
                // so that multi-location items appear under every location they belong to.
                const countItems = [
                  ...templateLocationRows.map((row, index) => {
                    const invItem = inventoryLookup[row.inventory_item_id];
                    const copiedParLevel = row.par_level ?? invItem?.par_level ?? null;
                    const copiedParDefaulted = row.par_defaulted ?? invItem?.par_defaulted ?? false;
                    const copiedParUnit = copiedParDefaulted
                      ? (invItem?.unit || row.par_unit || 'each')
                      : (row.par_unit || invItem?.unit || 'each');
                    const expectedQty = copiedParLevel != null ? String(copiedParLevel) : (invItem?.case_qty || '0');
                    console.log(`📋 Template row ${row.inventory_item_id} @ ${row.location}: PAR=${row.par_level ?? 'none'}`);
                    return {
                      count_session_id: sessionResult.id,
                      inventory_item_id: row.inventory_item_id,
                      item_snapshot: { ...(invItem || {}), location: row.location, par_level: copiedParLevel, par_unit: copiedParUnit, par_defaulted: copiedParDefaulted },
                      expected_quantity: expectedQty,
                      actual_quantity: '',
                      variance_quantity: 0,
                      count_notes: '',
                      is_counted: false,
                      par_level: copiedParLevel,
                      par_unit: copiedParUnit,
                      par_defaulted: copiedParDefaulted,
                      // Vendor minimums live on purchase_options — never seeded here.
                      min_order_qty: null,
                      min_order_unit: 'each',
                      location: row.location,
                      count_position: (index + 1) * 10,
                      created_at: new Date().toISOString(),
                    };
                  }),
                  ...extraItems.map((item, index) => {
                    const copiedParDefaulted = item.par_defaulted ?? item.item_snapshot?.par_defaulted;
                    const copiedParUnit = copiedParDefaulted === true
                      ? (item.item_snapshot?.unit || item.unit || item.par_unit || 'each')
                      : (item.par_unit || item.item_snapshot?.unit || item.unit || 'each');
                    return {
                      count_session_id: sessionResult.id,
                      inventory_item_id: item.id,
                      item_snapshot: item,
                      expected_quantity: item.case_qty || '0',
                      actual_quantity: '',
                      variance_quantity: 0,
                      count_notes: '',
                      is_counted: false,
                      par_level: item.par_level === '' || item.par_level == null ? null : item.par_level,
                      par_unit: copiedParUnit,
                      ...(typeof copiedParDefaulted === 'boolean'
                        ? { par_defaulted: copiedParDefaulted }
                        : {}),
                      // Vendor minimums live on purchase_options — never seeded here.
                      min_order_qty: null,
                      min_order_unit: 'each',
                      location: item.location || null,
                      count_position: (templateLocationRows.length + index + 1) * 10,
                      created_at: new Date().toISOString(),
                    };
                  }),
                ];

                // Debug logging to help diagnose foreign key issues
                console.log('🔍 Creating count items for session:', sessionResult.id);
                console.log('📋 Items being added to count:', countItems.length, '(template location rows:', templateLocationRows.length, '+ extra:', extraItems.length, ')');
                console.log('📊 Sample count items data:', countItems.slice(0, 3));

                const { error: itemsError } = await supabase
                  .from('count_items')
                  .insert(countItems);

                if (itemsError) {
                  console.error('Error creating count items:', itemsError);
                  console.error('Error details:', JSON.stringify(itemsError, null, 2));
                  // Remove the just-created session so it doesn't strand as an
                  // empty draft (best-effort; the alert stands either way).
                  await supabase.from('count_sessions').delete().eq('id', sessionResult.id);
                  alert('Error creating count items: ' + (itemsError.message || itemsError.details || JSON.stringify(itemsError)));
                  return;
                }
                
                console.log('Count session created successfully:', sessionResult);
                
                // Show success message and navigate to counting tool
                showToastMessage('Count Created Successfully');
                
                // Reset form
                setSelectedItems([]);
                setSelectedTemplatesForSession([]);
                setCountSession({
                  name: '',
                  session_date: new Date().toISOString().split('T')[0],
                  created_time: getCurrentLocalTime(),
                  count_commence_time: getCurrentLocalTime(),
                  notes: ''
                });
                
                // Reload sessions to show the new one
                loadCountSessions();
                
                // Navigate to counting tool
                setTimeout(() => {
                  window.location.href = 'count.html';
                }, 1500); // Give time for the success message to show
                
              } catch (error) {
                console.error('Error creating session:', error);
                console.error('Error details:', error.message || error);
                alert('Error creating session: ' + (error.message || JSON.stringify(error)));
              } finally {
                setLoading(false);
              }
            }}
            disabled={(selectedTemplatesForSession.length === 0 && selectedItems.length === 0) || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className={`feather ${isEditMode ? 'icon-save' : 'icon-play'} me-1`}></i>
                {isEditMode ? 'Update Session' : 'Start Counting'} ({(() => {
                  const templateItems = selectedTemplatesForSession.reduce((acc, template) => {
                    return acc + (Array.isArray(template.items) ? template.items.length : 0);
                  }, 0);
                  return templateItems + selectedItems.length;
                })()} items)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {misuseModalElement}
      <style dangerouslySetInnerHTML={{
        __html: `
          .template-tooltip-wrapper:hover .template-tooltip {
            opacity: 1 !important;
            visibility: visible !important;
          }

          .order-enabled-card::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-image: url('/images/Order_Stamp_image.png');
            background-size: 39px 39px;
            background-repeat: no-repeat;
            background-position: 92% 8%;
            opacity: 0.10;
            z-index: 0;
            pointer-events: none;
            transform: rotate(30deg);
            transform-origin: 92% 8%;
          }

          /* Bootstrap Dropdown Overrides to Match Site Theme */
          .dropdown-toggle {
            font-family: 'Open Sans', sans-serif !important;
            color: #5A4A42 !important;
            border-color: #D4C4B0 !important;
            min-width: 160px !important;
          }

          .dropdown-toggle:hover,
          .dropdown-toggle:focus {
            border-color: #C8B5A0 !important;
            color: #5A4A42 !important;
          }

          .dropdown-menu {
            font-family: 'Open Sans', sans-serif !important;
            border-color: #D4C4B0 !important;
            box-shadow: 0 2px 8px rgba(90, 74, 66, 0.1) !important;
            min-width: 160px !important;
          }

          .dropdown-item {
            color: #5A4A42 !important;
            font-family: 'Open Sans', sans-serif !important;
          }

          .dropdown-item:hover,
          .dropdown-item:focus {
            background-color: #FAF8F6 !important;
            color: #5A4A42 !important;
          }

          .dropdown-item.active,
          .dropdown-item:active {
            background-color: #FAF8F6 !important;
            color: #5A4A42 !important;
          }

          /* Custom Dropdown Styling to Match Site Theme */
          .template-sort-dropdown button,
          .template-filter-dropdown button {
            font-family: 'Open Sans', sans-serif !important;
            color: #5A4A42 !important;
            border-color: #D4C4B0 !important;
            background: #fff !important;
          }

          .template-sort-dropdown button:hover,
          .template-filter-dropdown button:hover {
            border-color: #C8B5A0 !important;
            color: #5A4A42 !important;
            background: #fff !important;
          }
        `
      }} />
      {(() => {
        switch (step) {
          case 'dashboard':
            return renderDashboard();
    // Commented out old template creator - replaced with location-based template creator
    // case 'create-template':
    //   return renderCreateTemplateStep();
    case 'location-template-creator':
      return (
        <GroupEditor
          editingGroup={editingGroupForEditor}
          onToast={showToastMessage}
          onSave={() => {
            loadTemplates();
            setEditingGroupForEditor(null);
            setStep('dashboard');
          }}
          onCancel={() => {
            setEditingGroupForEditor(null);
            setStep('dashboard');
          }}
        />
      );
    case 'customize-template':
      return renderCustomizeTemplateStep();
    case 'create-session':
      return renderCreateSessionStep();
    case 'create-schedule':
      return (
        <AutoScheduleCreator
          onSave={(scheduleData) => {
            console.log('Schedule saved:', scheduleData);
            showToastMessage(`Auto-schedule "${scheduleData.name}" created successfully!`, 'success');
            setStep('dashboard');
            setActiveTab('templates'); // Changed from schedules (tab removed)
            // Refresh the page data to show new schedule
            loadCountSessions();
            loadSchedules();
          }}
          onCancel={() => {
            setStep('dashboard');
            setActiveTab('templates'); // Changed from schedules (tab removed)
          }}
        />
      );
    case 'count':
      return (
        <CountInterface
          onCancel={() => setStep('dashboard')}
        />
      );
    case 'report':
      return (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Count Session Report</h5>
            <p className="text-muted mb-0">Session completed successfully!</p>
          </div>
          <div className="card-body">
            <div className="alert alert-success">
              <h6>Session Summary</h6>
              <ul className="mb-0">
                <li>Name: {countSession.name}</li>
                <li>Date: {countSession.session_date}</li>
                <li>Items Counted: {selectedItems.filter(item => item.actual_qty).length} / {selectedItems.length}</li>
                <li>Total Variance: {selectedItems.reduce((sum, item) => sum + (item.variance || 0), 0)}</li>
              </ul>
            </div>
            
            <h6>Variance Report</h6>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Expected</th>
                    <th>Actual</th>
                    <th>Variance</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.filter(item => item.variance !== 0).map(item => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td>{item.expected_qty || 0}</td>
                      <td>{item.actual_qty || 0}</td>
                      <td className={item.variance > 0 ? 'text-success' : 'text-danger'}>
                        {item.variance > 0 && '+'}{item.variance}
                      </td>
                      <td>{item.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="d-flex justify-content-between mt-4">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setStep('dashboard');
                  setSelectedItems([]);
                  setSelectedTemplatesForSession([]);
                  setCountSession({
                    name: '',
                    session_date: new Date().toISOString().split('T')[0],
                    created_time: getCurrentLocalTime(),
                    count_commence_time: getCurrentLocalTime(),
                    notes: ''
                  });
                }}
              >
                <i className="feather icon-arrow-left me-1"></i>
                Back to Dashboard
              </button>
              <button className="btn btn-secondary">
                <i className="feather icon-download me-1"></i>
                Export Report
              </button>
            </div>
          </div>
        </div>
      );
          default:
            return renderDashboard();
        }
      })()}

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="feather icon-archive me-2"></i>
                  Archive this template?
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowArchiveModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  Archiving hides this template from the active list and stops it from being used in new counts.
                  All completed counts and reports that reference it will remain intact and unaffected.
                </p>
                <p className="text-muted small mb-0">You can restore archived templates at any time from the Archive view.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowArchiveModal(false)}>Cancel</button>
                <button type="button" className="btn btn-warning" onClick={handleArchiveTemplates}>
                  <i className="feather icon-archive me-1"></i>
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete / Archive-only Modal — branches based on template history */}
      {showDeleteModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              {checkingTemplateHistory ? (
                <>
                  <div className="modal-header">
                    <h5 className="modal-title">Checking template history…</h5>
                    <button type="button" className="btn-close" onClick={() => { setShowDeleteModal(false); setTemplateDeleteDialogMode(null); }}></button>
                  </div>
                  <div className="modal-body text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Checking for completed count history…
                  </div>
                </>
              ) : templateDeleteDialogMode === 'has-history' ? (
                <>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="feather icon-alert-circle me-2 text-warning"></i>
                      This template has completed count history
                    </h5>
                    <button type="button" className="btn-close" onClick={() => { setShowDeleteModal(false); setTemplateDeleteDialogMode(null); }}></button>
                  </div>
                  <div className="modal-body">
                    <p>
                      This template has completed counts linked to it. Permanently deleting it would break those historical records, so deletion is not allowed.
                    </p>
                    <p className="mb-0">
                      <strong>Archive instead</strong> — archiving hides this template from active use while keeping all completed counts and reports intact.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowDeleteModal(false); setTemplateDeleteDialogMode(null); }}>Cancel</button>
                    <button type="button" className="btn btn-warning" onClick={() => handleArchiveSingleTemplate(templateToDelete)}>
                      <i className="feather icon-archive me-1"></i>
                      Archive
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-header">
                    <h5 className="modal-title">Delete this template?</h5>
                    <button type="button" className="btn-close" onClick={() => { setShowDeleteModal(false); setTemplateDeleteDialogMode(null); }}></button>
                  </div>
                  <div className="modal-body">
                    <p>This template has no completed count history and can be safely deleted.</p>
                    <p className="text-muted small mb-0">
                      The template will be permanently removed from this store. This action cannot be undone.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowDeleteModal(false); setTemplateDeleteDialogMode(null); }}>Cancel</button>
                    <button type="button" className="btn btn-danger" onClick={() => handleDeleteTemplate(templateToDelete)}>
                      <i className="feather icon-trash-2 me-1"></i>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive View Modal */}
      {showArchiveView && (
        <ArchiveView
          onClose={() => setShowArchiveView(false)}
          onUnarchive={unarchiveTemplate}
        />
      )}

      {/* Database Browser Modal */}
      {showDatabaseBrowser && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDatabaseBrowser(false);
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="feather icon-database me-2"></i>
                  Add Items from Database
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDatabaseBrowser(false)}
                ></button>
              </div>
              <div className="modal-body p-0" style={{ height: '70vh' }}>
                <DatabaseColumn
                  inventoryData={inventoryData}
                  currentItems={selectedItems}
                  onAddItem={handleAddFromDatabase}
                  onItemClick={handleDatabaseItemClick}
                  onFilteredListChange={setDatabaseFilteredItems}
                  config={getDatabaseColumnPreset('TEMPLATE_BUILDER')}
                  browserConfig={{
                    ...getDatabaseBrowserPreset('COUNT_SESSION'),
                    selectable: true,
                    browserApiRef: modalBrowserApiRef,
                    onCheckedItemsChange: setDatabaseCheckedItems
                  }}
                  styling={{
                    column: { height: '100%', border: 'none', borderRadius: 0 },
                    header: { borderBottom: '1px solid #dee2e6' }
                  }}
                />
              </div>
              <div className="modal-footer">
                <div className="d-flex justify-content-between align-items-center flex-grow-1">
                  <strong>Selected Items: {selectedItems.length}</strong>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleSelectAllFiltered}>
                      Select All{databaseFilteredItems.length > 0 && databaseFilteredItems.length < inventoryData.length ? ` (${databaseFilteredItems.length})` : ''}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleDeselectAllFiltered} disabled={databaseCheckedItems.length === 0}>
                      Deselect All
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: '#2e7d32', borderColor: '#2e7d32', color: '#fff' }}
                      onClick={handleAddSelectedItems}
                      disabled={databaseCheckedItems.length === 0}
                    >
                      Add Selected ({databaseCheckedItems.length})
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDatabaseBrowser(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type={toastType}
        duration={2000}
      />
    </>
  );
};

export default CountSessionManager;
